import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import StatTile from '../components/StatTile';
import { ViewModeToggle, useViewMode } from '../components/ViewModeToggle';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { 
  Plus, 
  Wrench,
  Trash2,
  Loader2,
  Truck,
  ClipboardCheck,
  Download,
  Edit,
  Camera,
  Image,
  X,
  Package,
  CheckCircle,
  XCircle,
  Clock,
  Eye,
  Printer,
  FileText,
  ZoomIn,
  CalendarIcon,
  ChevronUp,
  ChevronDown,
  MapPin,
  Mail,
  ArrowUpDown,
  RotateCcw,
  FolderOpen,
  Search
} from 'lucide-react';
import { toast } from 'sonner';
import { format, startOfWeek, startOfMonth } from 'date-fns';
import JobSelectionCard from '../components/JobSelectionCard';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar as CalendarWidget } from '../components/ui/calendar';
import { parseLocalDate } from '../lib/utils';
import { todayET, formatDateET, formatDateTimeET, dateInputValueET } from '../lib/dateHelpers';
import AssetManagement from '../components/AssetManagement';
import ChecklistRenderer, { flattenItems, initialValuesFor, summarizeChecklistItem, ASSET_LABEL_HINTS } from '../components/ChecklistRenderer';
import { SafeImage } from '../components/SafeImage';
import { resolveFileUrl } from '../lib/fileUtils';
import PhotoLightbox from '../components/PhotoLightbox';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FORM_TYPES = [
  // NOTE: `value` is what gets stored in db.maintenance_forms.form_type.
  // Kept as-is for backward compat with historical records. Only the
  // display label was updated to "Maintenance Request Form" (Feb 2026).
  { value: 'Maintenance', label: 'Maintenance Request Form' },
  { value: 'Grease', label: 'Grease Form' },
  // Fix (2026-08-30): 'Equipment Check In'/'Equipment Check Out' used
  // to live here too, opening this page's own generic dialog (a second,
  // separately-maintained implementation with a hardcoded checklist —
  // no per-category template, no Job Selection search, no auto-fill).
  // Retired: the two tiles below now route to the same Quick Check
  // In/Out flow used everywhere else (AssetManagement.jsx, via the
  // Dashboard's ?quickAction= URL param), so there's exactly one
  // Check In/Out implementation left. Existing historical records of
  // these two types are untouched and still open in the Edit dialog
  // below via the legacy-checklist branch.
  // Confined Space Evaluation — safety permit, not tied to equipment.
  // 2026-09-08: PERMANENTLY removed from this creatable dropdown.
  // Git history (e01aad31, 2026-08-25 → 581425fa, 2026-08-26, one day
  // later) shows this was deliberately hidden here specifically because
  // it was being rebuilt as its own page: CSEForm.js + the Field
  // Reports "Confined Space" tab (/confined-space). That IS the
  // "later phase... re-add when the rework lands" the hide commit
  // referred to — the rework landed on Field Reports, not back here.
  // A 2026-09-05 pass mis-read the hide as unexplained and re-enabled
  // it here; this reverts that. Field Reports is the one, correct home
  // going forward — do not re-add this entry again.
  //
  // TEMPLATE_BACKED_FORM_TYPES / FORM_TYPE_TEMPLATE_TITLE below and the
  // `formType !== 'Confined Space Evaluation'` branches further down
  // intentionally KEEP their CSE entries even though it's no longer
  // creatable here: GET /maintenance (this page's own "Forms" list)
  // returns every form_type from the shared collection, so an existing
  // CSE record (created via Field Reports) can still be opened here
  // for viewing — removing those would make it render via the wrong
  // legacy branch instead of just not being creatable.
];

const TEMPLATE_BACKED_FORM_TYPES = new Set([
  'Grease',
  'Maintenance',
  'Confined Space Evaluation',
]);

const FORM_TYPE_TEMPLATE_TITLE = {
  'Grease': 'Grease Form',
  'Maintenance': 'Maintenance Request Form',
  'Confined Space Evaluation': 'Confined Space Evaluation',
};

const getDefaultChecklist = (type) => {
  const checklists = {
    'Maintenance': [
      { item: 'Oil Level Check', checked: false, notes: '' },
      { item: 'Fluid Levels', checked: false, notes: '' },
      { item: 'Belt Condition', checked: false, notes: '' },
      { item: 'Tire Condition', checked: false, notes: '' },
      { item: 'Lights Working', checked: false, notes: '' },
      { item: 'Horn Working', checked: false, notes: '' }
    ],
    'Grease': [
      { item: 'Front End', checked: false, notes: '' },
      { item: 'Rear End', checked: false, notes: '' },
      { item: 'Drive Shaft', checked: false, notes: '' },
      { item: 'Steering Components', checked: false, notes: '' }
    ],
    'Equipment Check In': [
      { item: 'Visual Inspection', checked: false, notes: '' },
      { item: 'Damage Report', checked: false, notes: '' },
      { item: 'Fuel Level', checked: false, notes: '' },
      { item: 'Clean Interior', checked: false, notes: '' },
      { item: 'Keys Returned', checked: false, notes: '' }
    ],
    'Equipment Check Out': [
      { item: 'Pre-Trip Inspection', checked: false, notes: '' },
      { item: 'Safety Equipment Present', checked: false, notes: '' },
      { item: 'Fuel Level Noted', checked: false, notes: '' },
      { item: 'Mileage Recorded', checked: false, notes: '' },
      { item: 'Keys Received', checked: false, notes: '' }
    ]
  };
  return checklists[type] || [];
};

export default function Maintenance() {
  const { user } = useAuth();
  const navigate = useNavigate();
  // 2026-10-01: delete (table rows + Pending/Approved/Rejected cards)
  // is admin/super_user only — separate from `isMaintenanceManager`
  // (the named-approver list), which governs approve/reject instead.
  const canDeleteForms = user?.role === 'admin' || user?.role === 'super_user';
  // 2026-09-06: 'forms' (tile-picker + one merged table) replaced by
  // two dedicated per-type tabs — 'maintenance' and 'grease' — matching
  // Field Reports' per-type tab pattern. 'assets', 'pending', 'approved',
  // 'rejected' are unchanged.
  // 2026-10-02: supports deep-linking from a notification straight into
  // a tab (and optionally a specific item) via ?tab=pending&highlight=ID
  // — e.g. the "resubmitted" notification a rejecting manager gets.
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(() => searchParams.get('tab') || 'assets'); // 'assets', 'maintenance', 'grease', 'pending', 'approved', 'rejected'
  const [highlightId, setHighlightId] = useState(() => searchParams.get('highlight') || null);
  // 2026-09-06: at ~30 check-outs + 30 check-ins/day (~22k/year), these
  // two lists are now loaded a page at a time instead of loading the
  // full history on every visit — `total` always reflects the real
  // count (server-computed) even though only PAGE_SIZE rows are held
  // in memory until "Load More" is clicked.
  const PAGE_SIZE = 50;
  const [maintenanceForms, setMaintenanceForms] = useState([]);
  const [maintenanceTotal, setMaintenanceTotal] = useState(0);
  const [greaseForms, setGreaseForms] = useState([]);
  const [greaseTotal, setGreaseTotal] = useState(0);
  const [loadingMoreType, setLoadingMoreType] = useState(null); // 'Maintenance' | 'Grease' | null
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [isViewOnly, setIsViewOnly] = useState(false); // View-only mode for approved forms
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useViewMode('maintenance.viewMode');
  // Sort controls for the Pending/Approved/Rejected queues — mirrors the
  // sort pattern on the Jobs page. 'date_desc' (Newest First) is the
  // default so nothing changes until a manager picks a different sort.
  const [pendingSortBy, setPendingSortBy] = useState('date_desc');
  const [approvedSortBy, setApprovedSortBy] = useState('date_desc');
  const [rejectedSortBy, setRejectedSortBy] = useState('date_desc');
  // Human-readable "type" used both for display and for the Check In/Out
  // Type sort — works for asset transactions (checkin/checkout/pretrip)
  // and maintenance forms (grease/maintenance request/CSE) alike.
  const getItemTypeLabel = (item) => {
    if (item.source === 'maintenance') return item.form_type || 'Maintenance Form';
    return item.type === 'pretrip' ? 'Pre-Trip' : item.type === 'checkout' ? 'Check Out' : 'Check In';
  };
  // Shared sort for the three mixed (asset + maintenance form) queues.
  const sortQueueItems = (items, sortBy) => {
    const sorted = [...items];
    if (sortBy === 'date_asc') {
      sorted.sort((a, b) => new Date(a.created_at || a.updated_at) - new Date(b.created_at || b.updated_at));
    } else if (sortBy === 'type') {
      sorted.sort((a, b) => getItemTypeLabel(a).localeCompare(getItemTypeLabel(b))
        || new Date(b.created_at || b.updated_at) - new Date(a.created_at || a.updated_at));
    } else {
      sorted.sort((a, b) => new Date(b.created_at || b.updated_at) - new Date(a.created_at || a.updated_at));
    }
    return sorted;
  };
  const QueueSortSelect = ({ value, onChange, testId }) => (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="h-11 w-[170px] gap-2" data-testid={testId}>
        <ArrowUpDown className="w-4 h-4 text-slate-400 shrink-0" />
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="date_desc">Date: Newest First</SelectItem>
        <SelectItem value="date_asc">Date: Oldest First</SelectItem>
        <SelectItem value="type">Check In/Out Type</SelectItem>
      </SelectContent>
    </Select>
  );
  // 2026-09-06: search is per-tab now (Maintenance Request vs Grease
  // Form each have their own list) — reset it on tab switch so a term
  // typed on one tab doesn't silently carry over and filter the other.
  useEffect(() => { setSearchTerm(''); }, [activeTab]);
  const [editingForm, setEditingForm] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(null); // Track which item is uploading

  // "Files" panel state — populated only when the view-only dialog is
  // open for a saved maintenance form (drafts have no id yet). Grouped
  // in the render by `kind` (archived_pdf / signature / photo).
  const [filesList, setFilesList] = useState(null); // null=idle, []=loaded-empty, [...]=loaded
  const [filesLoading, setFilesLoading] = useState(false);

  // Photo lightbox state
  const [lightboxPhoto, setLightboxPhoto] = useState(null);
  const [lightboxPhotos, setLightboxPhotos] = useState([]); // All photos for navigation
  const [lightboxIndex, setLightboxIndex] = useState(0);

  // Manager review state
  const [isMaintenanceManager, setIsMaintenanceManager] = useState(false);
  // Archive tab (2026-10-03) — unified browse/search across every
  // approved maintenance form + equipment transaction with (or
  // missing) its permanent OneDrive PDF archive. Manager/admin only.
  const ARCHIVE_PAGE_SIZE = 25;
  const [archiveItems, setArchiveItems] = useState([]);
  const [archiveTotal, setArchiveTotal] = useState(0);
  const [archiveLoading, setArchiveLoading] = useState(false);
  const [archiveFilters, setArchiveFilters] = useState({ startDate: '', endDate: '', equipment: '', formType: 'all', submittedBy: '' });
  const [archivingId, setArchivingId] = useState(null);
  const fetchArchive = async (isLoadMore = false) => {
    setArchiveLoading(true);
    try {
      const params = { skip: isLoadMore ? archiveItems.length : 0, limit: ARCHIVE_PAGE_SIZE };
      if (archiveFilters.startDate) params.start_date = archiveFilters.startDate;
      if (archiveFilters.endDate) params.end_date = archiveFilters.endDate;
      if (archiveFilters.equipment) params.equipment = archiveFilters.equipment;
      if (archiveFilters.formType !== 'all') params.form_type = archiveFilters.formType;
      if (archiveFilters.submittedBy) params.submitted_by = archiveFilters.submittedBy;
      const res = await axios.get(`${API}/maintenance/archive`, { params });
      setArchiveItems(isLoadMore ? [...archiveItems, ...res.data.items] : res.data.items);
      setArchiveTotal(res.data.total);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load archive');
    } finally {
      setArchiveLoading(false);
    }
  };
  const handleArchivePdfNow = async (item) => {
    setArchivingId(item.id);
    try {
      const endpoint = item.source === 'asset'
        ? `${API}/assets/transactions/${item.id}/archive-pdf`
        : `${API}/maintenance/${item.id}/archive-pdf`;
      const res = await axios.post(endpoint);
      setArchiveItems((prev) => prev.map((r) => (r.id === item.id ? { ...r, archived_pdf_url: res.data.archived_pdf_url } : r)));
      toast.success('PDF archived to OneDrive');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save PDF to storage — try again shortly');
    } finally {
      setArchivingId(null);
    }
  };
  // Checkbox selection + bulk ZIP download (2026-10-03). Each row is
  // live-rendered into the zip server-side (same engine as "Email me
  // the PDF"), so this works regardless of whether archived_pdf_url is
  // set yet — download is not limited to already-archived rows.
  const [archiveSelectedKeys, setArchiveSelectedKeys] = useState(new Set());
  const [archiveDownloading, setArchiveDownloading] = useState(false);
  const archiveKey = (item) => `${item.source}_${item.id}`;
  const toggleArchiveSelect = (item) => {
    setArchiveSelectedKeys((prev) => {
      const next = new Set(prev);
      const key = archiveKey(item);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };
  const toggleArchiveSelectAllVisible = () => {
    setArchiveSelectedKeys((prev) => {
      const allVisible = archiveItems.every((item) => prev.has(archiveKey(item)));
      if (allVisible) return new Set();
      const next = new Set(prev);
      archiveItems.forEach((item) => next.add(archiveKey(item)));
      return next;
    });
  };
  const downloadZip = async (items, zipLabel) => {
    if (items.length === 0) {
      toast.error('Nothing to download');
      return;
    }
    if (items.length > 100) {
      toast.error(`${items.length} selected — narrow it down to 100 or fewer`);
      return;
    }
    setArchiveDownloading(true);
    try {
      const res = await axios.post(
        `${API}/maintenance/archive-export/zip-selected`,
        { items: items.map((i) => ({ id: i.id, source: i.source })) },
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `${zipLabel}.zip`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Downloaded ${items.length} form(s)`);
    } catch (e) {
      let detail = 'Failed to build ZIP';
      if (e.response?.data instanceof Blob) {
        try { detail = JSON.parse(await e.response.data.text()).detail || detail; } catch { /* ignore */ }
      } else {
        detail = e.response?.data?.detail || detail;
      }
      toast.error(detail);
    } finally {
      setArchiveDownloading(false);
    }
  };
  const handleDownloadSelected = () => {
    const selected = archiveItems.filter((item) => archiveSelectedKeys.has(archiveKey(item)));
    downloadZip(selected, `maintenance_archive_selected_${new Date().toISOString().slice(0, 10)}`);
  };
  const handleDownloadFiltered = async () => {
    setArchiveDownloading(true);
    try {
      const params = { skip: 0, limit: 100 };
      if (archiveFilters.startDate) params.start_date = archiveFilters.startDate;
      if (archiveFilters.endDate) params.end_date = archiveFilters.endDate;
      if (archiveFilters.equipment) params.equipment = archiveFilters.equipment;
      if (archiveFilters.formType !== 'all') params.form_type = archiveFilters.formType;
      if (archiveFilters.submittedBy) params.submitted_by = archiveFilters.submittedBy;
      const res = await axios.get(`${API}/maintenance/archive`, { params });
      if (res.data.total > 100) {
        toast.error(`${res.data.total} forms match these filters — narrow the date range to 100 or fewer`);
        return;
      }
      await downloadZip(res.data.items, `maintenance_archive_filtered_${new Date().toISOString().slice(0, 10)}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load filtered forms');
    } finally {
      setArchiveDownloading(false);
    }
  };
  // Quick date-range presets ("select a week/month") — local calendar
  // dates are precise enough for a shortcut button; exact ET boundary
  // handling already happens server-side in build_archive_zip.
  const applyArchiveDatePreset = (preset) => {
    // Local calendar-date formatting (not toISOString(), which converts
    // to UTC and can shift the date back by one near local midnight).
    const toISODate = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    const today = new Date();
    let start, end;
    if (preset === 'this_week') {
      start = new Date(today); start.setDate(today.getDate() - today.getDay());
      end = today;
    } else if (preset === 'last_week') {
      end = new Date(today); end.setDate(today.getDate() - today.getDay() - 1);
      start = new Date(end); start.setDate(end.getDate() - 6);
    } else if (preset === 'this_month') {
      start = new Date(today.getFullYear(), today.getMonth(), 1);
      end = today;
    } else if (preset === 'last_month') {
      start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      end = new Date(today.getFullYear(), today.getMonth(), 0);
    }
    setArchiveFilters((prev) => ({ ...prev, startDate: toISODate(start), endDate: toISODate(end) }));
  };
  const [pendingTransactions, setPendingTransactions] = useState([]);
  // Scroll the deep-linked item (from a notification's ?highlight=ID)
  // into view once its tab's data is loaded, then clear the highlight
  // after a few seconds — one-time effect per highlightId.
  useEffect(() => {
    if (!highlightId) return;
    const el = document.querySelector(`[data-testid="pending-card-${highlightId}"], [data-testid="pending-list-row-${highlightId}"]`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const t = setTimeout(() => setHighlightId(null), 5000);
    return () => clearTimeout(t);
  }, [highlightId, pendingTransactions, viewMode]);

  // 2026-09-06: Pending Review cards used to always render fully
  // expanded (full checklist + all photos), making the page very long
  // with 10+ items. Now collapsed by default; click a card to expand.
  const [expandedPendingIds, setExpandedPendingIds] = useState(new Set());
  const togglePendingExpand = (id) => {
    setExpandedPendingIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const [approvedTransactions, setApprovedTransactions] = useState([]);
  const [rejectedTransactions, setRejectedTransactions] = useState([]);
  // 2026-09-06: Approved/Rejected merge two independently-paginated
  // sources (asset check-out/in transactions + Maintenance/Grease
  // forms) into one date-sorted feed. Each source keeps its own skip
  // cursor + server total so "Load More" always fetches the next
  // PAGE_SIZE from BOTH sources — nothing is ever skipped, it's just
  // loaded in batches instead of all ~22k/year at once.
  const [approvedSkip, setApprovedSkip] = useState({ asset: 0, form: 0 });
  const [approvedTotals, setApprovedTotals] = useState({ asset: 0, form: 0 });
  const [rejectedSkip, setRejectedSkip] = useState({ asset: 0, form: 0 });
  const [rejectedTotals, setRejectedTotals] = useState({ asset: 0, form: 0 });
  const [loadingMoreApproved, setLoadingMoreApproved] = useState(false);
  const [loadingMoreRejected, setLoadingMoreRejected] = useState(false);
  const [loadingTransactions, setLoadingTransactions] = useState(false);
  const [processingId, setProcessingId] = useState(null);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [rejectingTransaction, setRejectingTransaction] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');

  // Delete confirmation dialog state
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [formToDelete, setFormToDelete] = useState(null);

  // Manager approval signature state
  const [showApprovalDialog, setShowApprovalDialog] = useState(false);
  const [approvingItem, setApprovingItem] = useState(null);
  const [approvalSignature, setApprovalSignature] = useState(null);
  const approvalSignatureRef = useRef(null);

  // Equipment list for dropdown
  const [equipmentList, setEquipmentList] = useState([]);
  // Round 6: Job header on Grease + Maintenance Request Form
  // dialogs (parity with PreCon/Locate/Soft Dig/CSE and the asset
  // check-in/out flows). Same shared <JobSelectionCard>, same data
  // source (/api/jobs/for-field-reports).
  const [availableJobs, setAvailableJobs] = useState([]);
  const [loadingJobs, setLoadingJobs] = useState(true);
  const [selectedJobId, setSelectedJobId] = useState('');
  const selectedJob = availableJobs.find(j => j.id === selectedJobId) || null;
  // "Your Jobs Today" — same source/logic as Time Tracker v2 and the
  // asset check-out/check-in flows (GET /time-blocks/assigned-jobs).
  const [myJobIds, setMyJobIds] = useState([]);
  const [selectedEquipment, setSelectedEquipment] = useState(null);
  const [useCustomEquipment, setUseCustomEquipment] = useState(false);

  const [formType, setFormType] = useState('');
  const [equipmentName, setEquipmentName] = useState('');
  const [equipmentId, setEquipmentId] = useState('');
  const [formDate, setFormDate] = useState(todayET());
  const [performedBy, setPerformedBy] = useState(user?.full_name || '');
  const [checklist, setChecklist] = useState([]);
  // Template-backed flow (Grease, Maintenance Request Form, Confined
  // Space Evaluation) — parallel state that lives alongside the
  // legacy `checklist` array. When `templateFlatItems` is non-empty
  // the form dialog renders <ChecklistRenderer> instead of the
  // legacy custom renderer and submits `checklist_values` instead
  // of `checklist`.
  const [templateFlatItems, setTemplateFlatItems] = useState([]);
  const [templateUpdatedAt, setTemplateUpdatedAt] = useState(null);
  const [templateValues, setTemplateValues] = useState({});
  const [templateTitle, setTemplateTitle] = useState('');
  // Team + assets for dropdown hydration in template-backed forms.
  const [teamMembers, setTeamMembers] = useState([]);
  // Round 16 (2026-09-04): deliberately NOT filtered by isFieldUsable
  // here — maintenance-only equipment (field_use=false, no Check-Out/
  // Check-In) must still be linkable on Maintenance Request / Grease
  // forms. isFieldUsable is only applied to the actual Check-Out/
  // Check-In pickers (AssetDetail.js / AssetManagement.jsx).
  const [assetsForSelect, setAssetsForSelect] = useState([]);
  // Bug #4 fix (2026-09-06): cache of every checklist template, used
  // by the Pending Review card to resolve labels for checklist_values
  // answers (Grease/Maintenance/CSE) without a per-card network call.
  const [checklistTemplatesList, setChecklistTemplatesList] = useState([]);
  useEffect(() => {
    let mounted = true;
    Promise.all([
      axios.get(`${API}/team`).catch(() => ({ data: [] })),
      axios.get(`${API}/assets`).catch(() => ({ data: [] })),
      axios.get(`${API}/jobs/for-field-reports`).catch(() => ({ data: [] })),
      axios.get(`${API}/assets/equipment-categories`).catch(() => ({ data: [] })),
      axios.get(`${API}/time-blocks/assigned-jobs?tz_offset=${new Date().getTimezoneOffset()}`).catch(() => ({ data: [] })),
      axios.get(`${API}/checklist-templates`).catch(() => ({ data: [] })),
    ]).then(([tm, as, jobs, cats, myJobs, tpls]) => {
      if (!mounted) return;
      setTeamMembers(Array.isArray(tm.data) ? tm.data : []);
      setAssetsForSelect(
        (Array.isArray(as.data) ? as.data : [])
          .filter(a => a && a.name)
      );
      setAvailableJobs(Array.isArray(jobs.data) ? jobs.data : []);
      setMyJobIds((Array.isArray(myJobs.data) ? myJobs.data : []).map(j => j.id));
      setChecklistTemplatesList(Array.isArray(tpls.data) ? tpls.data : []);
      setLoadingJobs(false);
    });
    return () => { mounted = false; };
  }, []);

  // Bug #3 fix (2026-09-06): the Maintenance Request Form template has
  // its OWN "Select Equipment Needing Maintenance" select item — a
  // second, independent piece of equipment-selection state from the
  // generic "Select Equipment" picker above it. There's no real reason
  // for the two to diverge (same physical equipment), so pre-fill the
  // template field from whatever's picked in the generic one instead
  // of asking the user to pick equipment twice. Still fully editable
  // afterward if it's ever genuinely different.
  const applyEquipmentToValues = (flatItems, values, labelStr, { onlyIfEmpty = false } = {}) => {
    if (!labelStr || !flatItems || flatItems.length === 0) return values;
    let changed = false;
    const next = { ...values };
    flatItems.forEach((item, idx) => {
      if (item.type !== 'select') return;
      const label = (item.label || '').toLowerCase();
      if (!ASSET_LABEL_HINTS.some(h => label.includes(h))) return;
      const current = next[idx] || {};
      if (onlyIfEmpty && current.value) return;
      next[idx] = { ...current, value: labelStr };
      changed = true;
    });
    return changed ? next : values;
  };
  const equipmentLabelFor = (equipment) => (
    equipment ? (equipment.asset_number ? `${equipment.name} (${equipment.asset_number})` : equipment.name) : ''
  );

  // Fetch the seeded template by title for one of the three
  // template-backed form types. No-op for legacy types.
  const loadTemplateForType = async (type) => {
    const title = FORM_TYPE_TEMPLATE_TITLE[type];
    if (!title) {
      setTemplateFlatItems([]);
      setTemplateValues({});
      setTemplateTitle('');
      setTemplateUpdatedAt(null);
      return null;
    }
    try {
      const res = await axios.get(`${API}/checklist-templates`);
      const list = Array.isArray(res.data) ? res.data : [];
      const tpl = list.find(t => t.title === title);
      if (!tpl) {
        toast.error(`Template "${title}" not found — falling back to legacy form`);
        return null;
      }
      const flat = flattenItems(tpl.sections || []);
      const vals = initialValuesFor(flat);
      setTemplateFlatItems(flat);
      setTemplateValues(vals);
      setTemplateTitle(tpl.title || '');
      setTemplateUpdatedAt(tpl.updated_at || null);
      return { flat, vals, title: tpl.title };
    } catch (err) {
      console.error('Failed to load checklist template:', err);
      return null;
    }
  };
  const [notes, setNotes] = useState('');
  const [mileage, setMileage] = useState('');
  const [condition, setCondition] = useState('');

  // Signature state
  const signatureCanvasRef = React.useRef(null);
  // Fix (2026-08-30): form creation (and the sequential form_number it
  // consumes) is now deferred until the user's first explicit Save/
  // Submit — handleAddNew/handleTypeChange no longer auto-POST a draft
  // on dialog open. Nothing is created until then, so there is nothing
  // to clean up on cancel/close/unmount.
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);

  // File input ref for photo uploads
  const photoInputRefs = React.useRef({});

  // Fetch equipment list. Reads from /api/assets (the current 24+
  // record collection) — the legacy /api/equipment endpoint is now
  // empty and was showing only 3-4 stale rows in the dropdown, which
  // is why techs couldn't find their real trucks / vac trucks / etc.
  const fetchEquipment = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/assets`);
      setEquipmentList(response.data);
    } catch (error) {
      console.error('Error fetching equipment:', error);
    }
  }, []);

  // Handle equipment selection from dropdown
  const handleEquipmentSelect = (equipmentId) => {
    if (equipmentId === 'custom') {
      setUseCustomEquipment(true);
      setSelectedEquipment(null);
      setEquipmentName('');
      setEquipmentId('');
      return;
    }
    
    setUseCustomEquipment(false);
    const equipment = equipmentList.find(e => e.id === equipmentId);
    if (equipment) {
      setSelectedEquipment(equipment);
      setEquipmentName(equipment.name);
      // Auto-populate Equipment ID with VIN or Serial Number.
      // `model_number` is the legacy field name; `model` is the asset
      // schema field — support both so no info is dropped either way.
      const idParts = [];
      if (equipment.vin) idParts.push(`VIN: ${equipment.vin}`);
      if (equipment.serial_number) idParts.push(`S/N: ${equipment.serial_number}`);
      const modelStr = equipment.model_number || equipment.model;
      if (modelStr) idParts.push(`Model: ${modelStr}`);
      setEquipmentId(idParts.join(' | ') || equipment.id);
      // Bug #3 fix: pre-fill the Maintenance Request Form template's own
      // equipment select field from this same pick — see applyEquipmentToValues.
      setTemplateValues(prev => applyEquipmentToValues(templateFlatItems, prev, equipmentLabelFor(equipment)));
    }
  };

  const fetchFormsPage = useCallback(async (typeValue, skip, reset) => {
    const isMaint = typeValue === 'Maintenance';
    try {
      if (!reset) setLoadingMoreType(typeValue);
      const response = await axios.get(`${API}/maintenance`, {
        params: { form_type: typeValue, skip, limit: PAGE_SIZE }
      });
      const { items, total } = response.data;
      if (isMaint) {
        setMaintenanceForms(prev => reset ? items : [...prev, ...items]);
        setMaintenanceTotal(total);
      } else {
        setGreaseForms(prev => reset ? items : [...prev, ...items]);
        setGreaseTotal(total);
      }
    } catch (error) {
      console.error('Failed to fetch forms:', error);
    } finally {
      setLoadingMoreType(null);
    }
  }, []);

  const loadMoreForms = (typeValue) => {
    const skip = typeValue === 'Maintenance' ? maintenanceForms.length : greaseForms.length;
    fetchFormsPage(typeValue, skip, false);
  };

  useEffect(() => {
    (async () => {
      await Promise.all([
        fetchFormsPage('Maintenance', 0, true),
        fetchFormsPage('Grease', 0, true),
      ]);
      setLoading(false);
    })();
    fetchEquipment();
    checkIfMaintenanceManager();
    // Fix (2026-08-30): Pending Review is visible to everyone now, so
    // fetch it unconditionally too (used to only fetch for managers) —
    // otherwise the tab's badge count stays at 0 until a non-manager
    // clicks into the tab once.
    fetchPendingTransactions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchEquipment]);

  // Check if current user is a maintenance manager (can approve maintenance forms)
  const checkIfMaintenanceManager = async () => {
    try {
      const response = await axios.get(`${API}/maintenance-settings`);
      const managerIds = response.data.manager_ids || [];
      // Check if user is in manager list, is admin/super_user, OR has can_approve_maintenance_forms permission
      const hasPermission = user?.permissions?.can_approve_maintenance_forms;
      const isManager = managerIds.includes(user?.id) || ['admin', 'super_user'].includes(user?.role) || hasPermission;
      setIsMaintenanceManager(isManager);
      
      if (isManager) {
        fetchApprovedTransactions();
        fetchRejectedTransactions();
      }
    } catch (error) {
      console.error('Failed to check manager status:', error);
    }
  };

  // Fetch pending transactions for manager review
  const fetchPendingTransactions = async () => {
    setLoadingTransactions(true);
    try {
      // Fetch both asset transactions and maintenance forms
      const [assetRes, formsRes] = await Promise.all([
        axios.get(`${API}/assets/transactions/pending-approval`),
        axios.get(`${API}/maintenance/status/pending`)
      ]);
      // Combine both types with a source field
      const assetPending = assetRes.data.map(t => ({ ...t, source: 'asset' }));
      const formsPending = formsRes.data.map(f => ({ ...f, source: 'maintenance' }));
      setPendingTransactions([...assetPending, ...formsPending].sort((a, b) => 
        new Date(b.created_at || b.updated_at) - new Date(a.created_at || a.updated_at)
      ));
    } catch (error) {
      console.error('Failed to fetch pending transactions:', error);
    } finally {
      setLoadingTransactions(false);
    }
  };

  // mode: true (default) = full reset to page 1 (initial load / tab click).
  // false = "Load More" — append the next page.
  // 'refresh' = re-fetch exactly what's already loaded (same count, skip=0)
  // and replace in place — used after approve/reject so a manager who has
  // already clicked "Load More" doesn't get collapsed back to page 1.
  const fetchApprovedTransactions = async (mode = true) => {
    const isLoadMore = mode === false;
    const isRefresh = mode === 'refresh';
    const assetSkip = isLoadMore ? approvedSkip.asset : 0;
    const formSkip = isLoadMore ? approvedSkip.form : 0;
    const assetLimit = isRefresh ? Math.max(approvedSkip.asset, PAGE_SIZE) : PAGE_SIZE;
    const formLimit = isRefresh ? Math.max(approvedSkip.form, PAGE_SIZE) : PAGE_SIZE;
    if (isLoadMore) setLoadingMoreApproved(true);
    try {
      const [assetRes, formsRes] = await Promise.all([
        axios.get(`${API}/assets/transactions/approved`, { params: { skip: assetSkip, limit: assetLimit } }),
        axios.get(`${API}/maintenance/status/approved`, { params: { skip: formSkip, limit: formLimit } })
      ]);
      const assetApproved = assetRes.data.items.map(t => ({ ...t, source: 'asset' }));
      const formsApproved = formsRes.data.items.map(f => ({ ...f, source: 'maintenance' }));
      const sortFn = (a, b) => new Date(b.reviewed_at || b.updated_at) - new Date(a.reviewed_at || a.updated_at);
      setApprovedTransactions(prev => {
        const merged = isLoadMore ? [...prev, ...assetApproved, ...formsApproved] : [...assetApproved, ...formsApproved];
        return merged.sort(sortFn);
      });
      setApprovedTotals({ asset: assetRes.data.total, form: formsRes.data.total });
      setApprovedSkip({
        asset: isLoadMore ? assetSkip + assetApproved.length : assetApproved.length,
        form: isLoadMore ? formSkip + formsApproved.length : formsApproved.length,
      });
    } catch (error) {
      console.error('Failed to fetch approved transactions:', error);
    } finally {
      setLoadingMoreApproved(false);
    }
  };

  // Archive Export — a zip of one PDF per approved form, filtered by
  // form type and/or date range so it stays usable as records pile up.
  const [showArchiveExportDialog, setShowArchiveExportDialog] = useState(false);
  const [archiveExportTypes, setArchiveExportTypes] = useState({
    checkout: true, checkin: true, pretrip: true, maintenance: true,
  });
  const [archiveExportDateFrom, setArchiveExportDateFrom] = useState('');
  const [archiveExportDateTo, setArchiveExportDateTo] = useState('');
  const [downloadingArchiveZip, setDownloadingArchiveZip] = useState(false);
  const applyArchivePreset = (preset) => {
    const todayStr = todayET();
    const today = new Date(`${todayStr}T12:00:00`); // noon avoids DST/UTC-parsing edge cases
    const from = preset === 'week' ? startOfWeek(today, { weekStartsOn: 1 }) : startOfMonth(today);
    setArchiveExportDateFrom(format(from, 'yyyy-MM-dd'));
    setArchiveExportDateTo(todayStr);
  };
  const handleArchiveExport = async () => {
    const selectedTypes = Object.entries(archiveExportTypes).filter(([, v]) => v).map(([k]) => k);
    if (selectedTypes.length === 0) {
      toast.error('Select at least one form type');
      return;
    }
    setDownloadingArchiveZip(true);
    const toastId = toast.loading('Building archive ZIP…');
    try {
      const res = await axios.get(`${API}/maintenance/archive-export/zip`, {
        params: {
          form_types: selectedTypes.join(','),
          date_from: archiveExportDateFrom || undefined,
          date_to: archiveExportDateTo || undefined,
        },
        responseType: 'blob',
        timeout: 600_000,
      });
      const blob = new Blob([res.data], { type: 'application/zip' });
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = `maintenance_archive_${todayET()}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objectUrl);
      const successCount = parseInt(res.headers['x-zip-success-count'], 10);
      const failureCount = parseInt(res.headers['x-zip-failure-count'], 10);
      if (!Number.isNaN(successCount) && failureCount > 0) {
        toast.warning(`ZIP downloaded — ${successCount} rendered, ${failureCount} failed (see manifest.txt).`, { id: toastId });
      } else if (!Number.isNaN(successCount)) {
        toast.success(`ZIP downloaded — ${successCount} form${successCount === 1 ? '' : 's'} rendered.`, { id: toastId });
      } else {
        toast.success('ZIP downloaded — check your Downloads folder.', { id: toastId });
      }
      setShowArchiveExportDialog(false);
    } catch (err) {
      let detail = 'Failed to build ZIP';
      if (err.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          detail = JSON.parse(text).detail || detail;
        } catch { /* leave default */ }
      } else if (err.response?.data?.detail) {
        detail = err.response.data.detail;
      }
      toast.error(detail, { id: toastId });
    } finally {
      setDownloadingArchiveZip(false);
    }
  };

  // Same mode contract as fetchApprovedTransactions above.
  const fetchRejectedTransactions = async (mode = true) => {
    const isLoadMore = mode === false;
    const isRefresh = mode === 'refresh';
    const assetSkip = isLoadMore ? rejectedSkip.asset : 0;
    const formSkip = isLoadMore ? rejectedSkip.form : 0;
    const assetLimit = isRefresh ? Math.max(rejectedSkip.asset, PAGE_SIZE) : PAGE_SIZE;
    const formLimit = isRefresh ? Math.max(rejectedSkip.form, PAGE_SIZE) : PAGE_SIZE;
    if (isLoadMore) setLoadingMoreRejected(true);
    try {
      const [assetRes, formsRes] = await Promise.all([
        axios.get(`${API}/assets/transactions/rejected`, { params: { skip: assetSkip, limit: assetLimit } }),
        axios.get(`${API}/maintenance/status/rejected`, { params: { skip: formSkip, limit: formLimit } })
      ]);
      const assetRejected = assetRes.data.items.map(t => ({ ...t, source: 'asset' }));
      const formsRejected = formsRes.data.items.map(f => ({ ...f, source: 'maintenance' }));
      const sortFn = (a, b) => new Date(b.rejected_at || b.updated_at) - new Date(a.rejected_at || a.updated_at);
      setRejectedTransactions(prev => {
        const merged = isLoadMore ? [...prev, ...assetRejected, ...formsRejected] : [...assetRejected, ...formsRejected];
        return merged.sort(sortFn);
      });
      setRejectedTotals({ asset: assetRes.data.total, form: formsRes.data.total });
      setRejectedSkip({
        asset: isLoadMore ? assetSkip + assetRejected.length : assetRejected.length,
        form: isLoadMore ? formSkip + formsRejected.length : formsRejected.length,
      });
    } catch (error) {
      console.error('Failed to fetch rejected transactions:', error);
    } finally {
      setLoadingMoreRejected(false);
    }
  };

  // Open approval dialog with signature requirement
  const handleApprove = (item) => {
    setApprovingItem(item);
    setApprovalSignature(null);
    setShowApprovalDialog(true);
  };

  // Actually submit the approval with manager signature
  const submitApproval = async () => {
    if (!approvingItem) return;
    
    // Get signature data
    const signatureData = getApprovalSignatureData();
    if (!signatureData) {
      toast.error('Manager signature is required for approval');
      return;
    }
    
    setProcessingId(approvingItem.id);
    try {
      if (approvingItem.source === 'maintenance') {
        await axios.post(`${API}/maintenance/${approvingItem.id}/approve`, {
          signature: signatureData
        });
      } else {
        await axios.post(`${API}/assets/transactions/${approvingItem.id}/approve`, {
          manager_signature: signatureData
        });
      }
      toast.success('Approved successfully');
      fetchPendingTransactions();
      fetchApprovedTransactions('refresh');
      setShowApprovalDialog(false);
      setApprovingItem(null);
    } catch (error) {
      console.error('Failed to approve:', error);
      toast.error(error.response?.data?.detail || 'Failed to approve');
    } finally {
      setProcessingId(null);
    }
  };

  // Get approval signature data
  const getApprovalSignatureData = () => {
    if (!approvalSignatureRef.current || !approvalSignature) return null;
    return approvalSignatureRef.current.toDataURL('image/png');
  };

  // Clear approval signature
  const clearApprovalSignature = () => {
    const canvas = approvalSignatureRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    }
    setApprovalSignature(null);
  };

  // Initialize approval signature canvas
  const initApprovalSignatureCanvas = (canvas) => {
    if (!canvas) return;
    approvalSignatureRef.current = canvas;
    const ctx = canvas.getContext('2d');
    ctx.strokeStyle = '#1e293b';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  };

  // Handle approval signature drawing
  const handleApprovalSignatureStart = (e) => {
    const canvas = approvalSignatureRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    ctx.beginPath();
    ctx.moveTo(x, y);
    canvas.isDrawing = true;
    setApprovalSignature(true);
  };

  const handleApprovalSignatureMove = (e) => {
    const canvas = approvalSignatureRef.current;
    if (!canvas || !canvas.isDrawing) return;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const handleApprovalSignatureEnd = () => {
    const canvas = approvalSignatureRef.current;
    if (canvas) canvas.isDrawing = false;
  };

  // Reject a transaction or form
  const handleReject = async () => {
    if (!rejectingTransaction) return;
    
    setProcessingId(rejectingTransaction.id);
    try {
      if (rejectingTransaction.source === 'maintenance') {
        await axios.post(`${API}/maintenance/${rejectingTransaction.id}/reject`, {
          reason: rejectionReason
        });
      } else {
        await axios.post(`${API}/assets/transactions/${rejectingTransaction.id}/reject`, {
          rejection_reason: rejectionReason
        });
      }
      toast.success('Rejected');
      setShowRejectDialog(false);
      setRejectingTransaction(null);
      setRejectionReason('');
      fetchPendingTransactions();
      fetchRejectedTransactions('refresh');
    } catch (error) {
      console.error('Failed to reject:', error);
      toast.error(error.response?.data?.detail || 'Failed to reject transaction');
    } finally {
      setProcessingId(null);
    }
  };

  const openRejectDialog = (transaction) => {
    setRejectingTransaction(transaction);
    setRejectionReason('');
    setShowRejectDialog(true);
  };

  const resetForm = () => {
    // Fix (2026-08-30): creation is deferred until first Save/Submit —
    // nothing is created on dialog open, so closing/cancelling has
    // nothing to clean up.
    setEditingForm(null);
    setFormType('');
    setEquipmentName('');
    setEquipmentId('');
    setSelectedEquipment(null);
    setUseCustomEquipment(false);
    setFormDate(todayET());
    setPerformedBy(user?.full_name || '');
    setChecklist([]);
    setTemplateFlatItems([]);
    setTemplateValues({});
    setTemplateTitle('');
    setNotes('');
    setMileage('');
    setCondition('');
    setHasSignature(false);
    setIsViewOnly(false);
    setSelectedJobId('');
    // Reset the "Files" panel so the next opened form doesn't briefly
    // flash stale files while the fetch is in flight.
    setFilesList(null);
    setFilesLoading(false);
    // Clear signature canvas
    if (signatureCanvasRef.current) {
      const ctx = signatureCanvasRef.current.getContext('2d');
      ctx.clearRect(0, 0, signatureCanvasRef.current.width, signatureCanvasRef.current.height);
    }
  };

  // Iter130 fix: Radix <Dialog onOpenChange> only fires for Radix-
  // initiated closes (Escape, overlay click, its own X). A Cancel
  // button that calls setShowDialog(false) directly does NOT trigger
  // onOpenChange, so the resetForm() draft cleanup was a no-op on the
  // most common close path. Every explicit close now routes through
  // this single helper so cleanup can never be skipped again.
  const handleCloseDialog = () => {
    resetForm();
    setShowDialog(false);
  };

  // Signature drawing functions
  const getCoordinates = (e) => {
    const canvas = signatureCanvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    if (e.touches) {
      return {
        x: (e.touches[0].clientX - rect.left) * scaleX,
        y: (e.touches[0].clientY - rect.top) * scaleY
      };
    }
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY
    };
  };

  const startDrawing = (e) => {
    e.preventDefault();
    const canvas = signatureCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const { x, y } = getCoordinates(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    const canvas = signatureCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const { x, y } = getCoordinates(e);
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.strokeStyle = '#000';
    ctx.lineTo(x, y);
    ctx.stroke();
    setHasSignature(true);
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearSignature = () => {
    const canvas = signatureCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
  };

  const getSignatureData = () => {
    const canvas = signatureCanvasRef.current;
    if (!canvas || !hasSignature) return null;
    return canvas.toDataURL('image/png');
  };

  const handleAddNew = async (type) => {
    resetForm();
    if (type) {
      setFormType(type);
      // Fix (2026-08-30): no record is created here anymore — this
      // just loads the checklist locally so the dialog has fields to
      // render. The form (and its sequential form_number) is only
      // created on the first explicit Save/Submit, in handleSave/
      // handleSubmitForApproval, which already POST when editingForm
      // is null. Template-backed types (Grease, Maintenance Request
      // Form) load from db.checklist_templates via <ChecklistRenderer>;
      // legacy types keep the hardcoded checklist.
      const isTemplateBacked = TEMPLATE_BACKED_FORM_TYPES.has(type);
      if (isTemplateBacked) {
        await loadTemplateForType(type);
      } else {
        setChecklist(getDefaultChecklist(type).map(item => ({ ...item, photos: [] })));
      }
      setFormDate(todayET());
      setPerformedBy(user?.full_name || '');
    }
    setShowDialog(true);
  };

  // Prefer the immutable snapshot frozen at submit time on the form
  // doc (Feb 2026 template-editor safeguard). Fall back to a live
  // lookup by title for legacy pre-snapshot rows.
  const loadTemplateForExistingForm = async (form) => {
    const snap = form?.checklist_template_snapshot;
    if (snap && Array.isArray(snap.sections) && snap.sections.length > 0) {
      const flat = flattenItems(snap.sections || []);
      const vals = initialValuesFor(flat);
      setTemplateFlatItems(flat);
      setTemplateValues(vals);
      setTemplateTitle(snap.title || form?.checklist_template_title || '');
      return { flat, vals, title: snap.title };
    }
    return loadTemplateForType(form?.form_type);
  };

  // Fix (2026-08-30, task #73): resolve the "Select Equipment" dropdown
  // to the matching row so it doesn't sit on the unfilled placeholder
  // when opening an existing form — equipmentName/equipmentId (plain
  // text) were already pre-filled correctly, only this picker wasn't.
  // Match by the transaction's real asset id first (exact), falling
  // back to a name match for maintenance_forms records that never
  // carried an asset id; anything that matches neither (deleted/
  // retired equipment, or a genuinely custom entry) correctly falls
  // through to "Enter Custom Equipment" instead of a false match.
  const resolveEquipmentSelection = (form) => {
    const match = (form.equipment_asset_id && equipmentList.find(e => e.id === form.equipment_asset_id))
      || equipmentList.find(e => e.name === form.equipment_name);
    if (match) {
      setSelectedEquipment(match);
      setUseCustomEquipment(false);
    } else {
      setSelectedEquipment(null);
      setUseCustomEquipment(!!form.equipment_name);
    }
  };

  const handleEdit = async (form) => {
    setEditingForm(form);
    setFormType(form.form_type);
    setEquipmentName(form.equipment_name);
    setEquipmentId(form.equipment_id || '');
    setFormDate(form.date);
    setPerformedBy(form.performed_by || '');
    resolveEquipmentSelection(form);

    // Template-backed forms (Grease, Maintenance, Confined Space
    // Evaluation) restore into <ChecklistRenderer> when the record
    // was saved with checklist_values. Older records of these types
    // fall back to the legacy renderer via their `checklist` array.
    if (TEMPLATE_BACKED_FORM_TYPES.has(form.form_type) && form.checklist_values) {
      const tpl = await loadTemplateForExistingForm(form);
      // Merge saved values on top of the template's blank scaffold
      // so newly-added items in the template still render.
      if (tpl) {
        let merged = { ...tpl.vals, ...form.checklist_values };
        // Bug #3 fix: backfill the template's own equipment field for
        // older records that predate the auto-fill, without clobbering
        // a value that's already there.
        const matchedEq = (form.equipment_asset_id && assetsForSelect.find(e => e.id === form.equipment_asset_id))
          || assetsForSelect.find(e => e.name === form.equipment_name);
        merged = applyEquipmentToValues(tpl.flat, merged, equipmentLabelFor(matchedEq) || form.equipment_name, { onlyIfEmpty: true });
        setTemplateValues(merged);
      }
      setChecklist([]);
    } else {
      // Legacy path — ensure each checklist item has a photos array.
      const checklistWithPhotos = (form.checklist || getDefaultChecklist(form.form_type)).map(item => ({
        ...item,
        photos: item.photos || []
      }));
      setChecklist(checklistWithPhotos);
    }

    setNotes(form.notes || '');
    setMileage(form.mileage ? String(form.mileage) : '');
    setCondition(form.condition || '');
    setSelectedJobId(form.job_id || '');
    setIsViewOnly(false);
    setShowDialog(true);
  };

  // View form in read-only mode (for approved/rejected forms)
  const handleViewForm = async (form) => {
    setEditingForm(form);
    setFormType(form.form_type);
    setEquipmentName(form.equipment_name);
    setEquipmentId(form.equipment_id || '');
    setFormDate(form.date);
    setPerformedBy(form.performed_by || '');
    resolveEquipmentSelection(form);

    if (TEMPLATE_BACKED_FORM_TYPES.has(form.form_type) && form.checklist_values) {
      const tpl = await loadTemplateForExistingForm(form);
      if (tpl) {
        let merged = { ...tpl.vals, ...form.checklist_values };
        const matchedEq = (form.equipment_asset_id && assetsForSelect.find(e => e.id === form.equipment_asset_id))
          || assetsForSelect.find(e => e.name === form.equipment_name);
        merged = applyEquipmentToValues(tpl.flat, merged, equipmentLabelFor(matchedEq) || form.equipment_name, { onlyIfEmpty: true });
        setTemplateValues(merged);
      }
      setChecklist([]);
    } else {
      const checklistWithPhotos = (form.checklist || getDefaultChecklist(form.form_type)).map(item => ({
        ...item,
        photos: item.photos || []
      }));
      setChecklist(checklistWithPhotos);
    }

    setNotes(form.notes || '');
    setMileage(form.mileage ? String(form.mileage) : '');
    setCondition(form.condition || '');
    setSelectedJobId(form.job_id || '');
    setIsViewOnly(true);
    setShowDialog(true);

    // Fetch the "Files" list for any saved form — maintenance_forms AND
    // asset_transactions now both work since GET /maintenance/{id}/files
    // falls back to the asset_transactions collection (2026-10-03).
    // Drafts with no id yet are skipped since the endpoint would 404.
    if (form && form.id) {
      setFilesList(null);
      setFilesLoading(true);
      axios
        .get(`${API}/maintenance/${form.id}/files`)
        .then((r) => setFilesList(r.data?.files || []))
        .catch(() => setFilesList([]))
        .finally(() => setFilesLoading(false));
    }
  };

  // Convert an asset transaction (Check In/Check Out) into the maintenance
  // form shape the legacy dialog renderer expects. Shared by both the
  // read-only view (handleViewAssetTransaction) and the Edit path so
  // neither one re-feeds the raw new-shape checklist to a renderer that
  // only understands `item.item` / `item.checked`.
  const mapAssetTransactionToFormData = (transaction) => {
    // The checklist is stored using the template-backed shape (label/section/
    // type/value/selections) — `summarizeChecklistItem` also understands
    // the old pre-Feb-2026 flat `{item, checked}` shape for older records.
    const checklist = transaction.checklist || [];
    const summarized = checklist.map(summarizeChecklistItem);
    let mileageValue = null;
    let conditionValue = '';

    summarized.forEach(s => {
      const label = s.label.toLowerCase().trim();
      // Word-boundary match — avoids false positives like "Tank Condition"
      // matching a generic "vehicle condition" summary field.
      if (label.startsWith('mileage') && s.answerText) mileageValue = s.answerText;
      if (label.startsWith('condition') && s.answerText) conditionValue = s.answerText;
    });

    // Map checklist items to the format expected by the dialog. `checked`
    // must stay false for a failed (answered "no") item — the legacy
    // renderer only reads `checked`, so folding `failed` into it as true
    // would show a ticked/green box for a flagged inspection item.
    const mappedChecklist = summarized.map(s => ({
      item: s.label,
      checked: s.answered && !s.failed,
      status: s.failed ? 'fail' : (s.answered ? 'done' : 'pending'),
      notes: s.answerText || s.notes || '',
      value: s.answerText,
      photos: s.photos,
      // Fix 3 (2026-08-30): carry the template's photo:true flag through
      // so the legacy renderer below can gate the camera icon per row
      // instead of showing it unconditionally.
      photo: s.photo
    }));

    return {
      id: transaction.id,
      form_type: transaction.type === 'checkin' ? 'Equipment Check In' : transaction.type === 'pretrip' ? 'Pre-Trip Inspection' : 'Equipment Check Out',
      form_number: transaction.asset_number,
      equipment_name: transaction.asset_name,
      equipment_id: transaction.asset_number,
      // Fix (2026-08-30, task #73): the actual `assets` collection id,
      // so the Edit dialog can pre-select the right row in "Select
      // Equipment" instead of leaving it on the unfilled placeholder —
      // `equipment_id` above is the human-facing asset NUMBER, not this.
      equipment_asset_id: transaction.asset_id,
      // Fix (2026-08-30, task #73): transaction.created_at is a full UTC
      // ISO datetime — the native <input type="date"> below silently
      // renders blank for anything that isn't a plain YYYY-MM-DD, which
      // is why "Date" opened empty on every asset-transaction edit.
      date: dateInputValueET(transaction.created_at || transaction.updated_at),
      performed_by: transaction.user_name,
      checklist: mappedChecklist,
      notes: transaction.notes || '',
      mileage: mileageValue,
      condition: conditionValue || transaction.condition || '',
      signature_url: transaction.signature_url,
      submitter_name: transaction.user_name,
      submitted_at: transaction.created_at,
      reviewer_name: transaction.reviewed_by_name,
      reviewed_at: transaction.reviewed_at,
      reviewer_signature_url: transaction.manager_signature,
      // Fix 2 (2026-08-30): legacy asset transactions from before the
      // approval workflow existed (pre-Mar-2026) have no approval_status
      // field at all. Leaving it undefined made getStatusLabel() fall
      // through to its "Draft" default, so a fully-completed historical
      // check-in/check-out incorrectly displayed as "Draft" everywhere
      // this mapped status is read (dialog footer + PDF preview). These
      // records are done — no approval was ever pending — so treat a
      // missing approval_status as approved, not draft.
      status: transaction.approval_status || 'approved',
      source: 'asset'
    };
  };

  // View asset transaction (Check In/Check Out) - convert to maintenance form format for dialog
  const handleViewAssetTransaction = (transaction) => {
    handleViewForm(mapAssetTransactionToFormData(transaction));
  };

  // Edit a pending item — asset transactions must go through the same
  // label/answer normalization as the view dialog, otherwise the editor
  // shows blank checklist labels (same root cause as the P0 render bug).
  const handleEditPendingItem = (item) => {
    if (item.source === 'asset') {
      handleEdit(mapAssetTransactionToFormData(item));
    } else {
      handleEdit(item);
    }
  };

  // Open photo in lightbox
  const openLightbox = (photoUrl, allPhotos = [], index = 0) => {
    setLightboxPhoto(photoUrl);
    setLightboxPhotos(allPhotos);
    setLightboxIndex(index);
  };

  // Close lightbox
  const closeLightbox = () => {
    setLightboxPhoto(null);
    setLightboxPhotos([]);
    setLightboxIndex(0);
  };


  const handleTypeChange = async (type) => {
    setFormType(type);
    if (!editingForm) {
      // Fix (2026-08-30): same deferred-creation fix as handleAddNew —
      // just load the checklist locally, no POST. Template-backed
      // types (Grease, Maintenance Request Form) must load their
      // fields from db.checklist_templates via loadTemplateForType() —
      // otherwise templateFlatItems stays empty and the render falls
      // back to the legacy hardcoded checklist, silently hiding the
      // template's select items (including the field_use-filtered
      // equipment picker).
      const isTemplateBacked = TEMPLATE_BACKED_FORM_TYPES.has(type);
      if (isTemplateBacked) {
        await loadTemplateForType(type);
      } else {
        setChecklist(getDefaultChecklist(type).map(item => ({ ...item, photos: [] })));
      }
    }
  };

  const handleChecklistChange = (index, field, value) => {
    const newChecklist = [...checklist];
    newChecklist[index] = { ...newChecklist[index], [field]: value };
    setChecklist(newChecklist);
  };

  // Handle photo upload for a checklist item
  // Template-backed photo upload — stateless (round 15 fix): uploads
  // straight to storage via /maintenance/checklist-photo (no form_id
  // needed) and stores the returned URL in templateValues[idx].photos
  // locally. Persisted to the DB once Save Draft/Submit sends the full
  // checklist_values payload — mirrors AssetDetail.js's asset checklist
  // photo flow, which never required a save-first step either.
  const handleTemplatePhotoUpload = async (index, file) => {
    setUploadingPhoto(index);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const response = await axios.post(
        `${API}/maintenance/checklist-photo?form_type=${encodeURIComponent(formType || 'Maintenance')}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      setTemplateValues(prev => ({
        ...prev,
        [index]: {
          ...(prev[index] || {}),
          photos: [...((prev[index] || {}).photos || []), response.data.url],
        },
      }));
      toast.success('Photo uploaded');
    } catch (error) {
      toast.error('Failed to upload photo');
      console.error('Photo upload error:', error);
    } finally {
      setUploadingPhoto(null);
    }
  };

  const handlePhotoUpload = async (index, file) => {
    setUploadingPhoto(index);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(
        `${API}/maintenance/checklist-photo?form_type=${encodeURIComponent(formType || 'Maintenance')}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      // Update local checklist with new photo
      const newChecklist = [...checklist];
      if (!newChecklist[index].photos) {
        newChecklist[index].photos = [];
      }
      newChecklist[index].photos.push(response.data.url);
      setChecklist(newChecklist);
      
      toast.success('Photo uploaded');
    } catch (error) {
      toast.error('Failed to upload photo');
      console.error('Photo upload error:', error);
    } finally {
      setUploadingPhoto(null);
    }
  };

  // Handle photo delete from checklist item
  // Round 15: legacy checklist photos are local-state-only until Save/
  // Submit persists the whole checklist array (same as the upload side
  // above) — no per-photo DELETE call needed, and none is possible
  // before the form has an id anyway.
  const handlePhotoDelete = (index, photoUrl) => {
    const newChecklist = [...checklist];
    newChecklist[index].photos = newChecklist[index].photos.filter(p => p !== photoUrl);
    setChecklist(newChecklist);
  };

  const handleSave = async () => {
    if (!formType || !equipmentName) {
      toast.error('Please fill in required fields');
      return;
    }

    setSaving(true);

    // Fix (2026-08-30): asset-sourced forms (Check In/Check Out/
    // Pre-Trip) are asset_transactions documents, not maintenance_forms
    // — PUT /api/maintenance/{id} 404s for them ("Form not found").
    // Route corrections to the transaction's own update endpoint
    // instead. Checklist answers aren't re-sent here on purpose (see
    // backend endpoint's docstring) — only the freeform fields are
    // correctable from this dialog.
    if (editingForm?.source === 'asset') {
      try {
        await axios.put(`${API}/assets/transactions/${editingForm.id}`, {
          equipment_name: equipmentName,
          equipment_id: equipmentId,
          notes: notes,
        });
        toast.success(
          editingForm.status === 'approved'
            ? 'Correction saved (approval preserved).'
            : 'Changes saved'
        );
        setShowDialog(false);
        resetForm();
        if (activeTab === 'pending') fetchPendingTransactions();
        else if (activeTab === 'approved') fetchApprovedTransactions('refresh');
        else if (activeTab === 'rejected') fetchRejectedTransactions('refresh');
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to save changes');
      } finally {
        setSaving(false);
      }
      return;
    }

    try {
      const isTemplateBacked = TEMPLATE_BACKED_FORM_TYPES.has(formType) && templateFlatItems.length > 0;
      const formData = {
        form_type: formType,
        equipment_name: equipmentName,
        equipment_id: equipmentId,
        date: formDate,
        performed_by: performedBy,
        checklist: isTemplateBacked ? [] : checklist,
        checklist_values: isTemplateBacked ? templateValues : undefined,
        checklist_template_title: isTemplateBacked ? templateTitle : undefined,
        notes: notes,
        mileage: mileage ? parseFloat(mileage) : null,
        condition: condition
        ,
        // Round 6: job context (Grease + Maintenance Request Form).
        // Sent nulls when no job selected — legacy non-linked entries
        // still POST cleanly.
        job_id: selectedJob?.id || null,
        job_number: selectedJob?.job_number || null,
        job_service_address: selectedJob?.service_address || null,
        job_date: selectedJob?.date || null,
        job_assigned_team: selectedJob?.assigned_team_names || null
      };

      if (editingForm) {
        await axios.put(`${API}/maintenance/${editingForm.id}`, formData);
        toast.success(
          editingForm.status === 'approved'
            ? 'Correction saved (approval preserved).'
            : 'Form saved'
        );
      } else {
        await axios.post(`${API}/maintenance`, formData);
        toast.success('Form saved');
      }
      setShowDialog(false);
      resetForm();
      fetchFormsPage(formType, 0, true);
    } catch (error) {
      // Round 6: surface the 403 detail from enforce_edit_permissions
      // so admins/others see WHY a save was refused instead of a
      // generic "Failed to save form" toast.
      toast.error(error.response?.data?.detail || 'Failed to save form');
    } finally {
      setSaving(false);
    }
  };

  // Submit form for manager approval with signature
  const handleSubmitForApproval = async () => {
    if (!formType || !equipmentName) {
      toast.error('Please fill in required fields');
      return;
    }
    
    if (!hasSignature) {
      toast.error('Please sign the form before submitting');
      return;
    }

    // 2026-10-02: backend now also accepts this same /submit call when
    // status is "rejected" (resubmit path) — capture that up front so
    // we can show the right toast and refresh the Rejected/Pending
    // tabs instead of the Maintenance Forms list below.
    const wasRejected = editingForm?.status === 'rejected';

    setSaving(true);
    try {
      // First save the form if needed
      let formId = editingForm?.id;

      const isTemplateBacked = TEMPLATE_BACKED_FORM_TYPES.has(formType) && templateFlatItems.length > 0;
      const formData = {
        form_type: formType,
        equipment_name: equipmentName,
        equipment_id: equipmentId,
        date: formDate,
        performed_by: performedBy,
        checklist: isTemplateBacked ? [] : checklist,
        checklist_values: isTemplateBacked ? templateValues : undefined,
        checklist_template_title: isTemplateBacked ? templateTitle : undefined,
        notes: notes,
        mileage: mileage ? parseFloat(mileage) : null,
        condition: condition,
        // Bug #1 fix (2026-09-06): this payload was missing the job_*
        // fields that handleSave's formData already sends — submitting
        // via this button (the normal path for a first submission) was
        // silently dropping the selected job. Mirrors handleSave exactly.
        job_id: selectedJob?.id || null,
        job_number: selectedJob?.job_number || null,
        job_service_address: selectedJob?.service_address || null,
        job_date: selectedJob?.date || null,
        job_assigned_team: selectedJob?.assigned_team_names || null
      };

      if (editingForm) {
        await axios.put(`${API}/maintenance/${editingForm.id}`, formData);
        formId = editingForm.id;
      } else {
        const response = await axios.post(`${API}/maintenance`, formData);
        formId = response.data.id;
        // Fix (2026-08-30): remember the just-created record so that if
        // the /submit call below fails (e.g. signature upload error),
        // a retry click hits the `if (editingForm)` PUT branch above
        // instead of POSTing again — otherwise a retry would create a
        // second record and consume a second form_number.
        setEditingForm(response.data);
      }
      
      // Now submit for approval with signature
      const signatureData = getSignatureData();
      await axios.post(`${API}/maintenance/${formId}/submit`, {
        signature: signatureData
      });
      
      toast.success(wasRejected ? 'Form resubmitted for approval' : 'Form submitted for approval');
      setShowDialog(false);
      resetForm();
      if (wasRejected) {
        fetchRejectedTransactions('refresh');
        fetchPendingTransactions();
      } else {
        fetchFormsPage(formType, 0, true);
      }
    } catch (error) {
      console.error('Submit error:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit form');
    } finally {
      setSaving(false);
    }
  };

  // Resubmit a rejected equipment Check In/Out/Pre-Trip transaction —
  // no new signature needed (per user decision), just persist any edits
  // via the existing update endpoint, then flip approval_status back to
  // "pending" via the dedicated resubmit endpoint.
  const handleResubmitAsset = async () => {
    if (!editingForm?.id) return;
    setSaving(true);
    try {
      await axios.put(`${API}/assets/transactions/${editingForm.id}`, {
        equipment_name: equipmentName,
        equipment_id: equipmentId,
        notes: notes,
      });
      await axios.post(`${API}/assets/transactions/${editingForm.id}/resubmit`);
      toast.success('Resubmitted for approval');
      setShowDialog(false);
      resetForm();
      fetchRejectedTransactions('refresh');
      fetchPendingTransactions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resubmit');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (formId, source = 'maintenance') => {
    // Show confirmation dialog instead of window.confirm
    setFormToDelete({ id: formId, source });
    setShowDeleteDialog(true);
  };

  const confirmDelete = async () => {
    if (!formToDelete) return;

    try {
      if (formToDelete.source === 'asset') {
        await axios.delete(`${API}/assets/transactions/${formToDelete.id}`);
      } else {
        await axios.delete(`${API}/maintenance/${formToDelete.id}`);
      }
      toast.success('Form deleted');
      if (activeTab === 'pending') fetchPendingTransactions();
      else if (activeTab === 'approved') fetchApprovedTransactions('refresh');
      else if (activeTab === 'rejected') fetchRejectedTransactions('refresh');
      else {
        fetchFormsPage('Maintenance', 0, true);
        fetchFormsPage('Grease', 0, true);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete form');
      console.error('Delete error:', error);
    } finally {
      setShowDeleteDialog(false);
      setFormToDelete(null);
    }
  };

  const handleExport = async (typeValue) => {
    // 2026-09-06: export now pulls the FULL history for this type via
    // the paginated endpoint (looping until exhausted), independent of
    // how many rows are currently loaded on screen — otherwise export
    // would silently only include whatever page happens to be loaded.
    let allForms = [];
    let skip = 0;
    let total = Infinity;
    try {
      while (allForms.length < total) {
        const response = await axios.get(`${API}/maintenance`, {
          params: { form_type: typeValue, skip, limit: 500 }
        });
        allForms = allForms.concat(response.data.items);
        total = response.data.total;
        skip += response.data.items.length;
        if (response.data.items.length === 0) break;
      }
    } catch (error) {
      toast.error('Failed to export forms');
      console.error('Export error:', error);
      return;
    }

    if (allForms.length === 0) {
      toast.error('No forms to export');
      return;
    }

    const headers = ['Form Type', 'Equipment', 'Equipment ID', 'Date', 'Performed By', 'Mileage', 'Condition', 'Notes'];
    const rows = allForms.map(form => [
      form.form_type,
      form.equipment_name,
      form.equipment_id || '',
      form.date,
      form.performed_by,
      form.mileage || '',
      form.condition || '',
      (form.notes || '').replace(/"/g, '""')
    ]);
    
    const csvContent = [headers.join(','), ...rows.map(r => r.map(v => `"${v}"`).join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `maintenance-forms-${todayET()}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success('CSV downloaded to your Downloads folder');
  };

  // 2026-09-06: live counts for the two dedicated per-type tabs —
  // server-computed totals, accurate regardless of how many rows are
  // actually loaded on screen.
  const maintenanceCount = maintenanceTotal;
  const greaseCount = greaseTotal;

  // Shared renderer for both dedicated form-type tabs — replaces the
  // old tile-picker + single merged table with one filtered list per
  // tab (search + card/list toggle), matching Field Reports' per-type
  // tab pattern. The "Form Type" column/badge is dropped since every
  // row in a given tab is already that type.
  const renderFormTypeTab = (typeValue, typeLabel) => {
    const typeForms = typeValue === 'Maintenance' ? maintenanceForms : greaseForms;
    const typeTotal = typeValue === 'Maintenance' ? maintenanceTotal : greaseTotal;
    const t = (searchTerm || '').trim().toLowerCase();
    // Note: search only filters the rows already loaded on this page —
    // it does not search the full ~22k/year history server-side. Load
    // more pages first if you're searching for an older record.
    const displayForms = t
      ? typeForms.filter(f =>
          (f.form_number || '').toLowerCase().includes(t) ||
          (f.equipment_name || '').toLowerCase().includes(t) ||
          (f.equipment_id || '').toLowerCase().includes(t) ||
          (f.performed_by || '').toLowerCase().includes(t)
        )
      : typeForms;
    const hasMore = typeForms.length < typeTotal;

    return (
      <>
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h2 className="text-2xl font-bold text-slate-900 font-['Barlow_Condensed'] tracking-tight">
              {typeLabel}
            </h2>
            <p className="text-slate-500 text-sm mt-1">
              {typeValue === 'Grease' ? 'Track grease/lube service records' : 'Track equipment maintenance and inspections'}
              {typeTotal > 0 && ` — showing ${typeForms.length} of ${typeTotal}`}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={() => handleExport(typeValue)} data-testid="export-maintenance-btn">
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </Button>
            <Button
              className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold"
              onClick={() => handleAddNew(typeValue)}
              data-testid="new-maintenance-form-btn"
            >
              <Plus className="w-5 h-5 mr-2" />
              New {typeLabel}
            </Button>
          </div>
        </div>

        <Card className="border-slate-200">
          <CardContent className="p-3">
            <div className="flex flex-col md:flex-row gap-3">
              <div className="relative flex-1">
                <svg className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/></svg>
                <Input
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder={`Search form #, equipment, performed by...`}
                  className="pl-9"
                  data-testid="maintenance-search"
                />
              </div>
              <ViewModeToggle value={viewMode} onChange={setViewMode} />
            </div>
          </CardContent>
        </Card>

        {displayForms.length === 0 ? (
          <Card className="border-slate-200">
            <CardContent className="py-12 text-center">
              <ClipboardCheck className="w-12 h-12 mx-auto mb-4 text-slate-300" />
              <h3 className="text-lg font-medium text-slate-900 mb-1">No {typeLabel} records</h3>
              <p className="text-slate-500">
                {typeForms.length === 0 ? `Create your first ${typeLabel.toLowerCase()} to track equipment` : 'No forms on this page match your search'}
              </p>
            </CardContent>
          </Card>
        ) : viewMode === 'cards' ? (
          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
            {displayForms.map(form => (
              <Card
                key={form.id}
                className="border-slate-200 hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => handleViewForm(form)}
                data-testid={`maintenance-card-${form.id}`}
              >
                <CardContent className="p-4 space-y-2">
                  <Badge variant="outline" className="font-mono text-amber-600 border-amber-300">
                    {form.form_number || '-'}

                  </Badge>
                  <h3 className="font-semibold text-slate-900 truncate">
                    {form.equipment_name || 'Unnamed Equipment'}
                    {form.equipment_id && <span className="text-slate-400 text-xs ml-2">#{form.equipment_id}</span>}
                  </h3>
                  <p className="text-xs text-slate-500">
                    {format(parseLocalDate(form.date), 'MMM d, yyyy')} • {form.performed_by || 'N/A'}
                  </p>
                  {form.condition && (
                    <p className="text-xs text-slate-500">Condition: <span className="text-slate-700">{form.condition}</span></p>
                  )}
                  <div className="flex gap-1 pt-1" onClick={(e) => e.stopPropagation()}>
                    <Button variant="ghost" size="sm" className="h-11 w-11 p-0" onClick={() => handleViewForm(form)} data-testid={`view-maintenance-card-${form.id}`} title="Quick Preview"><Eye className="w-4 h-4" /></Button>
                    <Button variant="ghost" size="sm" className="h-11 w-11 p-0" onClick={() => navigate(`/maintenance/${form.id}/preview`)} title="Print Preview"><Printer className="w-4 h-4" /></Button>
                    <Button variant="ghost" size="sm" className="h-11 w-11 p-0" onClick={() => handleEdit(form)} data-testid={`edit-maintenance-card-${form.id}`} title="Edit"><Edit className="w-4 h-4" /></Button>
                    {canDeleteForms && (
                      <Button variant="ghost" size="sm" className="h-11 w-11 p-0 text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => handleDelete(form.id)} title="Delete"><Trash2 className="w-4 h-4" /></Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="border-slate-200">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Form #</TableHead>
                      <TableHead>Equipment</TableHead>
                      <TableHead className="hidden lg:table-cell">Date</TableHead>
                      <TableHead className="hidden lg:table-cell">Performed By</TableHead>
                      <TableHead className="hidden xl:table-cell">Condition</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {displayForms.map(form => (
                      <TableRow key={form.id}>
                        <TableCell>
                          <div className="flex flex-col">
                            <span className="font-mono text-xs text-amber-600 font-medium">
                              {form.form_number || '-'}
                            </span>
                            <span className="md:hidden text-xs text-slate-500 mt-1">
                              {form.equipment_name}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell className="font-medium">
                          {form.equipment_name}
                          {form.equipment_id && (
                            <span className="text-slate-500 ml-1 text-xs">#{form.equipment_id}</span>
                          )}
                        </TableCell>
                        <TableCell className="hidden lg:table-cell text-sm">{format(parseLocalDate(form.date), 'MMM d, yyyy')}</TableCell>
                        <TableCell className="hidden lg:table-cell text-sm">{form.performed_by}</TableCell>
                        <TableCell className="hidden xl:table-cell text-sm">{form.condition || '-'}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleViewForm(form); }}
                              data-testid={`view-maintenance-${form.id}`}
                              className="h-11 w-11 p-0"
                              title="Quick Preview"
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => { e.preventDefault(); e.stopPropagation(); navigate(`/maintenance/${form.id}/preview`); }}
                              className="h-11 w-11 p-0"
                              title="Print Preview"
                            >
                              <Printer className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleEdit(form); }}
                              data-testid={`edit-maintenance-${form.id}`}
                              className="h-11 w-11 p-0"
                              title="Edit"
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            {canDeleteForms && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-red-600 hover:text-red-700 hover:bg-red-50 h-11 w-11 p-0"
                                onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleDelete(form.id); }}
                                data-testid={`delete-maintenance-${form.id}`}
                                title="Delete"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        )}

        {hasMore && !t && (
          <div className="flex justify-center">
            <Button
              variant="outline"
              onClick={() => loadMoreForms(typeValue)}
              disabled={loadingMoreType === typeValue}
              data-testid={`load-more-${typeValue.toLowerCase()}-btn`}
            >
              {loadingMoreType === typeValue ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Loading...</>
              ) : (
                `Load More (${typeForms.length} of ${typeTotal})`
              )}
            </Button>
          </div>
        )}
      </>
    );
  };


  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-32 bg-slate-200 animate-pulse rounded" />
        <div className="h-64 bg-slate-200 animate-pulse rounded-lg" />
      </div>
    );
  }

  const sortedPendingTransactions = sortQueueItems(pendingTransactions, pendingSortBy);
  const sortedApprovedTransactions = sortQueueItems(approvedTransactions, approvedSortBy);
  const sortedRejectedTransactions = sortQueueItems(rejectedTransactions, rejectedSortBy);

  return (
    <div className="space-y-6" data-testid="maintenance-page">
      {/* Page Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900 font-['Barlow_Condensed'] tracking-tight">
            Equipment
          </h1>
          <p className="text-slate-500 mt-1">Manage company assets and maintenance records</p>
        </div>
        {/* 2026-09-06: direct path to the templates that back these forms
            — previously only reachable via Administration, with no link
            from Maintenance itself. */}
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => navigate('/equipment-overview')}
            data-testid="maintenance-equipment-overview-link"
          >
            <MapPin className="w-4 h-4 mr-2" />
            Equipment Overview
          </Button>
          <Button
            variant="outline"
            onClick={() => navigate('/services?tab=checklist-templates')}
            data-testid="maintenance-checklist-templates-link"
          >
            <FileText className="w-4 h-4 mr-2" />
            Checklist Templates
          </Button>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-2 border-b border-slate-200 pb-2 overflow-x-auto">
        <button
          onClick={() => setActiveTab('assets')}
          className={`px-4 py-2 rounded-t-lg font-medium transition-colors flex items-center gap-2 whitespace-nowrap ${
            activeTab === 'assets'
              ? 'bg-amber-100 text-amber-800 border-b-2 border-amber-500'
              : 'text-slate-600 hover:bg-slate-100'
          }`}
          data-testid="assets-tab"
        >
          <Package className="w-4 h-4" />
          Equipment
        </button>
        {/* 2026-09-06: two dedicated per-type tabs with live counts,
            replacing the old single "Maintenance Forms" tab (tile
            picker + one merged table + Type dropdown) — same pattern
            as PreCon/Locate/Soft Dig tabs on Field Reports. */}
        <button
          onClick={() => setActiveTab('maintenance')}
          className={`px-4 py-2 rounded-t-lg font-medium transition-colors flex items-center gap-2 whitespace-nowrap ${
            activeTab === 'maintenance'
              ? 'bg-amber-100 text-amber-800 border-b-2 border-amber-500'
              : 'text-slate-600 hover:bg-slate-100'
          }`}
          data-testid="maintenance-request-tab"
        >
          <Wrench className="w-4 h-4" />
          Maintenance Request
          <span className="bg-slate-200 text-slate-700 text-xs px-2 py-0.5 rounded-full">
            {maintenanceCount}
          </span>
        </button>
        <button
          onClick={() => setActiveTab('grease')}
          className={`px-4 py-2 rounded-t-lg font-medium transition-colors flex items-center gap-2 whitespace-nowrap ${
            activeTab === 'grease'
              ? 'bg-emerald-100 text-emerald-800 border-b-2 border-emerald-500'
              : 'text-slate-600 hover:bg-slate-100'
          }`}
          data-testid="grease-form-tab"
        >
          <ClipboardCheck className="w-4 h-4" />
          Grease Form
          <span className="bg-slate-200 text-slate-700 text-xs px-2 py-0.5 rounded-full">
            {greaseCount}
          </span>
        </button>
        
        {/* Fix (2026-08-30): Pending Review is now visible to everyone,
            matching the Maintenance Forms tab's existing precedent
            (every form visible to every user, edit rights enforced by
            the backend) — needed so the original submitter of a
            pending Check In/Out/Pre-Trip can reach the Edit button on
            their own item while it's pending, same as they already
            could for a pending Maintenance/Grease form. Approve/Reject
            stay manager-only (gated per-button below); Approved/
            Rejected tabs stay manager-only too (unchanged). */}
        <button
          onClick={() => { setActiveTab('pending'); fetchPendingTransactions(); }}
          className={`px-4 py-2 rounded-t-lg font-medium transition-colors flex items-center gap-2 whitespace-nowrap ${
            activeTab === 'pending'
              ? 'bg-orange-100 text-orange-800 border-b-2 border-orange-500'
              : 'text-slate-600 hover:bg-slate-100'
          }`}
          data-testid="pending-tab"
        >
          <Clock className="w-4 h-4" />
          Pending Review
          {pendingTransactions.length > 0 && (
            <span className="bg-orange-500 text-white text-xs px-2 py-0.5 rounded-full">
              {pendingTransactions.length}
            </span>
          )}
        </button>

        {/* Fix (2026-08-30): Approved tab now visible to everyone too,
            matching Pending Review's precedent — the original
            submitter can view (read-only, edit stays admin/super_user-
            only) their own approved equipment forms, same as
            Maintenance/Grease forms already worked via the general
            Maintenance Forms tab. Rejected stays manager-only —
            explicitly out of scope for this round. */}
        <button
          onClick={() => { setActiveTab('approved'); fetchApprovedTransactions(); }}
          className={`px-4 py-2 rounded-t-lg font-medium transition-colors flex items-center gap-2 whitespace-nowrap ${
            activeTab === 'approved'
              ? 'bg-green-100 text-green-800 border-b-2 border-green-500'
              : 'text-slate-600 hover:bg-slate-100'
          }`}
          data-testid="approved-tab"
        >
          <CheckCircle className="w-4 h-4" />
          Approved
        </button>

        {/* Rejected tab — opened to everyone (2026-10-02): non-managers
            see only their own rejected items (backend-filtered) so they
            can Resubmit after fixing the issue; managers/admins see all,
            same precedent as Pending/Approved above. */}
        <button
          onClick={() => { setActiveTab('rejected'); fetchRejectedTransactions(); }}
          className={`px-4 py-2 rounded-t-lg font-medium transition-colors flex items-center gap-2 whitespace-nowrap ${
            activeTab === 'rejected'
              ? 'bg-red-100 text-red-800 border-b-2 border-red-500'
              : 'text-slate-600 hover:bg-slate-100'
          }`}
          data-testid="rejected-tab"
        >
          <XCircle className="w-4 h-4" />
          Rejected
        </button>

        {/* Archive tab (2026-10-03) — manager/admin only. Unified
            search across every approved form's permanent PDF snapshot
            so managers don't have to open each form individually. */}
        {isMaintenanceManager && (
          <button
            onClick={() => { setActiveTab('archive'); fetchArchive(); }}
            className={`px-4 py-2 rounded-t-lg font-medium transition-colors flex items-center gap-2 whitespace-nowrap ${
              activeTab === 'archive'
                ? 'bg-amber-100 text-amber-800 border-b-2 border-amber-500'
                : 'text-slate-600 hover:bg-slate-100'
            }`}
            data-testid="archive-tab"
          >
            <FolderOpen className="w-4 h-4" />
            Archive
          </button>
        )}
      </div>

      {/* Tab Content */}
      {activeTab === 'assets' ? (
        <AssetManagement />
      ) : activeTab === 'pending' ? (
        /* Pending Review Tab Content */
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-slate-900 font-['Barlow_Condensed']">Pending Review</h2>
              <p className="text-slate-500 text-sm">Equipment check-in/check-out forms awaiting approval</p>
            </div>
            <div className="flex items-center gap-3">
              <QueueSortSelect value={pendingSortBy} onChange={setPendingSortBy} testId="pending-sort-select" />
              <ViewModeToggle value={viewMode} onChange={setViewMode} />
              <button onClick={fetchPendingTransactions} className="text-slate-500 hover:text-slate-700">
                <Loader2 className={`w-5 h-5 ${loadingTransactions ? 'animate-spin' : ''}`} />
              </button>
            </div>
          </div>
          
          {loadingTransactions ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
            </div>
          ) : pendingTransactions.length === 0 ? (
            <div className="text-center py-12 bg-slate-50 rounded-lg">
              <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-3" />
              <p className="text-slate-600 font-medium">All caught up!</p>
              <p className="text-slate-400 text-sm">No pending forms to review</p>
            </div>
          ) : viewMode === 'list' ? (
            <Card className="border-slate-200">
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Type</TableHead>
                        <TableHead>Item</TableHead>
                        <TableHead className="hidden lg:table-cell">Submitted By</TableHead>
                        <TableHead className="hidden lg:table-cell">Date</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {sortedPendingTransactions.map(item => (
                        <TableRow key={item.id} data-testid={`pending-list-row-${item.id}`} className={item.id === highlightId ? 'bg-amber-50 ring-2 ring-amber-400' : ''}>
                          <TableCell>
                            {item.source === 'maintenance' ? (
                              <Badge className="bg-purple-100 text-purple-800">{item.form_type}</Badge>
                            ) : (
                              <Badge className={
                                item.type === 'pretrip' ? 'bg-emerald-100 text-emerald-800'
                                  : item.type === 'checkout' ? 'bg-amber-100 text-amber-800' : 'bg-blue-100 text-blue-800'
                              }>
                                {item.type === 'pretrip' ? 'Pre-Trip' : item.type === 'checkout' ? 'Check Out' : 'Check In'}
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell className="font-medium">
                            {item.source === 'maintenance' ? item.equipment_name : item.asset_name}
                          </TableCell>
                          <TableCell className="hidden lg:table-cell text-sm">
                            {item.source === 'maintenance' ? item.submitter_name : item.user_name}
                          </TableCell>
                          <TableCell className="hidden lg:table-cell text-sm">{formatDateET(item.created_at || item.updated_at)}</TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1 flex-wrap">
                              {(isMaintenanceManager || user?.id === (item.source === 'maintenance' ? (item.submitted_by_id || item.created_by_id) : item.user_id)) && (
                                <Button variant="ghost" size="sm" className="h-11 w-11 p-0 text-slate-500 hover:bg-slate-100" onClick={() => handleEditPendingItem(item)} title="Edit">
                                  <Edit className="w-4 h-4" />
                                </Button>
                              )}
                              <Button variant="ghost" size="sm" className="h-11 w-11 p-0 text-slate-500 hover:bg-slate-100" onClick={() => navigate(`/maintenance/${item.id}/preview`)} title="PDF">
                                <Printer className="w-4 h-4" />
                              </Button>
                              {canDeleteForms && (
                                <Button variant="ghost" size="sm" className="h-11 w-11 p-0 text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => handleDelete(item.id, item.source)} title="Delete">
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              )}
                              {isMaintenanceManager && (
                                item.source === 'maintenance' ? (
                                  <Button
                                    size="sm"
                                    onClick={() => navigate(item.form_type === 'Confined Space Evaluation' ? `/confined-space/${item.id}/preview` : `/maintenance/${item.id}/preview`)}
                                    className="bg-green-600 hover:bg-green-500 text-white"
                                  >
                                    <Eye className="w-4 h-4 mr-1" />
                                    Review
                                  </Button>
                                ) : (
                                  <>
                                    <Button size="sm" onClick={() => handleApprove(item)} disabled={processingId === item.id} className="bg-green-600 hover:bg-green-500 text-white">
                                      {processingId === item.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                                    </Button>
                                    <Button variant="outline" size="sm" onClick={() => openRejectDialog(item)} disabled={processingId === item.id} className="border-red-300 text-red-600 hover:bg-red-50">
                                      <XCircle className="w-4 h-4" />
                                    </Button>
                                  </>
                                )
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {sortedPendingTransactions.map(item => {
                const isExpanded = expandedPendingIds.has(item.id);
                const hasTemplateAnswers = item.checklist_values && Object.keys(item.checklist_values).length > 0;
                const hasDetails = (item.checklist && item.checklist.length > 0) || hasTemplateAnswers || (item.photos && item.photos.length > 0) || item.signature_url || item.submitter_signature_url;
                // Bug #4 fix (2026-09-06): resolve the flat template items for
                // a checklist_values-based item (Grease/Maintenance/CSE) so its
                // answers can be summarized the same way the legacy `checklist`
                // array is below — previously this card only understood the
                // legacy shape, so template-backed forms (especially CSE, whose
                // whole content IS the checklist) showed nothing when expanded.
                const getFlatItemsForPendingItem = () => {
                  const snap = item.checklist_template_snapshot;
                  if (snap?.sections?.length) return flattenItems(snap.sections);
                  const title = item.checklist_template_title || FORM_TYPE_TEMPLATE_TITLE[item.form_type];
                  const tpl = checklistTemplatesList.find(t => t.title === title);
                  return tpl ? flattenItems(tpl.sections || []) : [];
                };
                return (
                <Card key={item.id} className={`border-orange-200 ${item.id === highlightId ? 'ring-2 ring-amber-400 shadow-lg' : ''}`} data-testid={`pending-card-${item.id}`}>
                  <CardContent className="p-4">
                    <div
                      className={`flex flex-col md:flex-row md:items-center justify-between gap-4 ${hasDetails ? 'cursor-pointer' : ''}`}
                      onClick={() => hasDetails && togglePendingExpand(item.id)}
                      data-testid={`pending-card-toggle-${item.id}`}
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          {item.source === 'maintenance' ? (
                            <Badge className="bg-purple-100 text-purple-800">
                              {item.form_type}
                            </Badge>
                          ) : (
                            <Badge className={
                              item.type === 'pretrip' ? 'bg-emerald-100 text-emerald-800'
                                : item.type === 'checkout' ? 'bg-amber-100 text-amber-800' : 'bg-blue-100 text-blue-800'
                            }>
                              {item.type === 'pretrip' ? 'Pre-Trip' : item.type === 'checkout' ? 'Check Out' : 'Check In'}
                            </Badge>
                          )}
                          <Badge variant="outline" className="text-xs">
                            {item.source === 'maintenance' ? 'Form' : 'Equipment'}
                          </Badge>
                          <span className="text-slate-500 text-sm">
                            {formatDateTimeET(item.created_at || item.updated_at)}
                          </span>
                        </div>
                        <h3 className="text-lg font-bold text-slate-900 font-['Barlow_Condensed']">
                          {item.source === 'maintenance' ? item.equipment_name : item.asset_name}
                        </h3>
                        <p className="text-slate-600">
                          {item.source === 'maintenance' ? `Form: ${item.form_number}` : `Equipment: ${item.asset_number}`}
                        </p>
                        <p className="text-slate-500 text-sm">
                          Submitted by: {item.source === 'maintenance' ? item.submitter_name : item.user_name}
                        </p>
                        {typeof item.duration_hours === 'number' && item.duration_hours > 0 && (
                          <p className="text-slate-500 text-sm">Duration: {item.duration_hours} hours</p>
                        )}
                        {item.previous_rejection_reason && (
                          <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 mt-1 inline-block" data-testid={`previous-rejection-note-${item.id}`}>
                            <strong>Previously rejected:</strong> {item.previous_rejection_reason}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-2 flex-wrap" onClick={(e) => e.stopPropagation()}>
                        <div className="flex gap-1">
                          {hasDetails && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-11 w-11 p-0 text-slate-500 hover:bg-slate-100"
                              onClick={() => togglePendingExpand(item.id)}
                              data-testid={`pending-expand-btn-${item.id}`}
                              title={isExpanded ? 'Hide Details' : 'View Details'}
                            >
                              {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                            </Button>
                          )}
                          {(isMaintenanceManager || user?.id === (item.source === 'maintenance' ? (item.submitted_by_id || item.created_by_id) : item.user_id)) && (
                            <Button 
                              variant="ghost" 
                              size="sm"
                              className="h-11 w-11 p-0 text-slate-500 hover:bg-slate-100"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEditPendingItem(item);
                              }}
                              data-testid={`edit-pending-form-btn-${item.id}`}
                              title="Edit"
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                          )}
                          <Button
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/maintenance/${item.id}/preview`);
                            }}
                            variant="ghost"
                            size="sm"
                            className="h-11 w-11 p-0 text-slate-500 hover:bg-slate-100"
                            data-testid={`pending-pdf-btn-${item.id}`}
                            title="PDF"
                          >
                            <Printer className="w-4 h-4" />
                          </Button>
                          {canDeleteForms && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-11 w-11 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDelete(item.id, item.source);
                              }}
                              data-testid={`delete-pending-btn-${item.id}`}
                              title="Delete"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                        {/* Fix (2026-08-30): Approve/Reject stay
                            manager-only now that this tab is visible
                            to everyone — Edit/PDF above are fine for
                            anyone to see, the backend enforces who can
                            actually save an edit. */}
                        {isMaintenanceManager && (
                          item.source === 'maintenance' ? (
                            // Bug #5 fix (2026-09-06): Grease/Maintenance
                            // Request/CSE approval no longer happens in the
                            // disconnected "Approve Form" popup — it now
                            // happens inline in the document itself (same
                            // sign-in-the-report pattern as PreCon), on the
                            // form's own preview page. This single button
                            // takes the manager there; Approve + Reject both
                            // live in that page's sticky bottom bar.
                            <Button
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(item.form_type === 'Confined Space Evaluation'
                                  ? `/confined-space/${item.id}/preview`
                                  : `/maintenance/${item.id}/preview`);
                              }}
                              className="bg-green-600 hover:bg-green-500 text-white"
                              data-testid={`pending-review-approve-btn-${item.id}`}
                            >
                              <Eye className="w-4 h-4 mr-2" />
                              Review &amp; Approve
                            </Button>
                          ) : (
                            <>
                              <Button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleApprove(item);
                                }}
                                disabled={processingId === item.id}
                                className="bg-green-600 hover:bg-green-500 text-white"
                                data-testid={`pending-approve-btn-${item.id}`}
                              >
                                {processingId === item.id ? (
                                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                                ) : (
                                  <CheckCircle className="w-4 h-4 mr-2" />
                                )}
                                Approve
                              </Button>
                              <Button
                                variant="outline"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openRejectDialog(item);
                                }}
                                disabled={processingId === item.id}
                                className="border-red-300 text-red-600 hover:bg-red-50"
                                data-testid={`pending-reject-btn-${item.id}`}
                              >
                                <XCircle className="w-4 h-4 mr-2" />
                                Reject
                              </Button>
                            </>
                          )
                        )}
                      </div>
                    </div>
                    {/* Checklist Preview with Photos — collapsed by
                        default (2026-09-06), only rendered when the
                        card is expanded via "View Details". */}
                    {isExpanded && item.checklist && item.checklist.length > 0 && (
                      <div className="mt-4 pt-4 border-t border-slate-200">
                        <p className="text-sm font-medium text-slate-700 mb-2">Inspection Checklist:</p>
                        <div className="space-y-2">
                          {item.checklist.map((rawChk, idx) => {
                            const chk = summarizeChecklistItem(rawChk);
                            return (
                            <div key={idx} className="bg-slate-50 p-2 rounded" data-testid={`pending-checklist-item-${item.id}-${idx}`} data-answer-state={chk.failed ? 'failed' : chk.answered ? 'answered' : 'unanswered'}>
                              <div className="flex items-center gap-2">
                                {chk.failed ? (
                                  <XCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                                ) : chk.answered ? (
                                  <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                                ) : (
                                  <XCircle className="w-4 h-4 text-amber-400 flex-shrink-0" />
                                )}
                                <span className="text-slate-700 font-medium" data-testid={`pending-checklist-label-${item.id}-${idx}`}>{chk.label}</span>
                                {chk.answerText && (
                                  <span className="text-slate-500 text-sm ml-2" data-testid={`pending-checklist-answer-${item.id}-${idx}`}>- {chk.answerText}</span>
                                )}
                              </div>
                              {/* Display Photos for this checklist item */}
                              {chk.photos && chk.photos.length > 0 && (
                                <div className="flex flex-wrap gap-2 mt-2 ml-6">
                                  {chk.photos.map((photoUrl, photoIdx) => (
                                    <button 
                                      key={photoIdx} 
                                      onClick={() => openLightbox(photoUrl, chk.photos, photoIdx)}
                                      className="block relative group"
                                    >
                                      <SafeImage
                                        src={resolveFileUrl(photoUrl, 'preview')} 
                                        alt={`${chk.label} - Photo ${photoIdx + 1}`}
                                        loading="lazy"
                                        decoding="async"
                                        className="w-48 h-48 object-cover rounded border-2 border-slate-200 hover:border-amber-400 transition-colors shadow-sm"
                                      />
                                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors rounded flex items-center justify-center">
                                        <ZoomIn className="w-5 h-5 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                                      </div>
                                    </button>
                                  ))}
                                </div>
                              )}
                            </div>
                          );})}
                        </div>
                      </div>
                    )}
                    {/* Bug #4 fix: template-backed answers (Grease/Maintenance/
                        CSE checklist_values) — same collapsed-by-default,
                        expand-on-click behavior as the legacy checklist above. */}
                    {isExpanded && hasTemplateAnswers && (() => {
                      const flat = getFlatItemsForPendingItem();
                      if (flat.length === 0) return null;
                      return (
                        <div className="mt-4 pt-4 border-t border-slate-200">
                          <p className="text-sm font-medium text-slate-700 mb-2">{item.checklist_template_title || item.form_type} Details:</p>
                          <div className="space-y-2">
                            {flat.map((flatItem, idx) => {
                              const raw = item.checklist_values[String(idx)] || item.checklist_values[idx] || {};
                              const chk = summarizeChecklistItem({ ...flatItem, ...raw });
                              if (!chk.answered && !(chk.photos && chk.photos.length > 0)) return null;
                              return (
                                <div key={idx} className="bg-slate-50 p-2 rounded" data-testid={`pending-template-item-${item.id}-${idx}`} data-answer-state={chk.failed ? 'failed' : 'answered'}>
                                  <div className="flex items-center gap-2">
                                    {chk.failed ? (
                                      <XCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                                    ) : (
                                      <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                                    )}
                                    <span className="text-slate-700 font-medium" data-testid={`pending-template-label-${item.id}-${idx}`}>{chk.label}</span>
                                    {chk.answerText && (
                                      <span className="text-slate-500 text-sm ml-2" data-testid={`pending-template-answer-${item.id}-${idx}`}>- {chk.answerText}</span>
                                    )}
                                  </div>
                                  {chk.photos && chk.photos.length > 0 && (
                                    <div className="flex flex-wrap gap-2 mt-2 ml-6">
                                      {chk.photos.map((photoUrl, photoIdx) => (
                                        <button
                                          key={photoIdx}
                                          onClick={() => openLightbox(photoUrl, chk.photos, photoIdx)}
                                          className="block relative group"
                                        >
                                          <SafeImage
                                            src={resolveFileUrl(photoUrl, 'preview')}
                                            alt={`${chk.label} - Photo ${photoIdx + 1}`}
                                            loading="lazy"
                                            decoding="async"
                                            className="w-48 h-48 object-cover rounded border-2 border-slate-200 hover:border-amber-400 transition-colors shadow-sm"
                                          />
                                          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors rounded flex items-center justify-center">
                                            <ZoomIn className="w-5 h-5 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                                          </div>
                                        </button>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })()}
                    {/* General Form Photos (if any) */}
                    {isExpanded && item.photos && item.photos.length > 0 && (
                      <div className="mt-4 pt-4 border-t border-slate-200">
                        <p className="text-sm font-medium text-slate-700 mb-2">Photos:</p>
                        <div className="flex flex-wrap gap-2">
                          {item.photos.map((photoUrl, photoIdx) => (
                            <button 
                              key={photoIdx} 
                              onClick={() => openLightbox(photoUrl, item.photos, photoIdx)}
                              className="block relative group"
                            >
                              <SafeImage
                                src={resolveFileUrl(photoUrl, 'preview')} 
                                alt={`Photo ${photoIdx + 1}`}
                                loading="lazy"
                                decoding="async"
                                className="w-48 h-48 object-cover rounded border-2 border-slate-200 hover:border-amber-400 transition-colors shadow-sm"
                              />
                              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors rounded flex items-center justify-center">
                                <ZoomIn className="w-5 h-5 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                              </div>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                    {isExpanded && (item.signature_url || item.submitter_signature_url) && (
                      <div className="mt-3">
                        <p className="text-sm font-medium text-slate-700 mb-1">Signature:</p>
                        <img src={item.signature_url || item.submitter_signature_url} alt="Signature" className="h-12 border rounded bg-white" />
                      </div>
                    )}
                  </CardContent>
                </Card>
              );})}
            </div>
          )}
        </div>
      ) : activeTab === 'approved' ? (
        /* Approved Tab Content */
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-slate-900 font-['Barlow_Condensed']">Approved Forms</h2>
              <p className="text-slate-500 text-sm">Forms that have been approved (kept indefinitely)</p>
            </div>
            <div className="flex items-center gap-3">
              <QueueSortSelect value={approvedSortBy} onChange={setApprovedSortBy} testId="approved-sort-select" />
              <ViewModeToggle value={viewMode} onChange={setViewMode} />
              <Button
                variant="outline"
                onClick={() => setShowArchiveExportDialog(true)}
                data-testid="archive-export-open-btn"
              >
                <FileText className="w-4 h-4 mr-2" />
                Archive Export
              </Button>
            </div>
          </div>
          
          {approvedTransactions.length === 0 ? (
            <div className="text-center py-12 bg-slate-50 rounded-lg">
              <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">No approved forms yet</p>
            </div>
          ) : viewMode === 'list' ? (
            <Card className="border-slate-200">
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Type</TableHead>
                        <TableHead>Item</TableHead>
                        <TableHead className="hidden lg:table-cell">Date</TableHead>
                        <TableHead className="hidden lg:table-cell">Submitted By</TableHead>
                        <TableHead className="hidden lg:table-cell">Approved By</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {sortedApprovedTransactions.map(item => (
                        <TableRow key={item.id} data-testid={`approved-list-row-${item.id}`}>
                          <TableCell>
                            {item.source === 'maintenance' ? (
                              <Badge className="bg-purple-100 text-purple-800">{item.form_type}</Badge>
                            ) : (
                              <Badge className={
                                item.type === 'pretrip' ? 'bg-emerald-100 text-emerald-800'
                                  : item.type === 'checkout' ? 'bg-amber-100 text-amber-800' : 'bg-blue-100 text-blue-800'
                              }>
                                {item.type === 'pretrip' ? 'Pre-Trip' : item.type === 'checkout' ? 'Check Out' : 'Check In'}
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell className="font-medium">
                            {item.source === 'maintenance'
                              ? `${item.equipment_name} (${item.form_number})`
                              : `${item.asset_name} (${item.asset_number})`}
                          </TableCell>
                          <TableCell className="hidden lg:table-cell text-sm">
                            {formatDateET(item.created_at || item.updated_at)}
                          </TableCell>
                          <TableCell className="hidden lg:table-cell text-sm">
                            {item.source === 'maintenance' ? item.submitter_name : item.user_name}
                          </TableCell>
                          <TableCell className="hidden lg:table-cell text-sm text-green-700">
                            {item.reviewer_name || item.reviewed_by_name}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <Button variant="ghost" size="sm" className="h-11 w-11 p-0 text-slate-500 hover:bg-slate-100" title="View"
                                onClick={() => item.source === 'asset' ? handleViewAssetTransaction(item) : handleViewForm(item)}>
                                <Eye className="w-4 h-4" />
                              </Button>
                              <Button variant="ghost" size="sm" className="h-11 w-11 p-0 text-slate-500 hover:bg-slate-100" title="PDF"
                                onClick={() => navigate(`/maintenance/${item.id}/preview`)}>
                                <Printer className="w-4 h-4" />
                              </Button>
                              {item.source === 'asset' && (
                                <Button variant="ghost" size="sm" className="h-11 w-11 p-0 text-slate-500 hover:bg-slate-100" title="Email me the PDF"
                                  onClick={async () => {
                                    try {
                                      const res = await axios.post(`${API}/assets/transactions/${item.id}/email-pdf`);
                                      toast.success(res.data?.message || 'PDF is on its way to your email');
                                    } catch (err) {
                                      toast.error(err.response?.data?.detail || 'Failed to email the PDF');
                                    }
                                  }}>
                                  <Mail className="w-4 h-4" />
                                </Button>
                              )}
                              {canDeleteForms && (
                                <Button variant="ghost" size="sm" className="h-11 w-11 p-0 text-red-600 hover:text-red-700 hover:bg-red-50" title="Delete"
                                  onClick={() => handleDelete(item.id, item.source)}>
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {sortedApprovedTransactions.map(item => (
                <Card key={item.id} className="border-green-200 bg-green-50/30">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          {item.source === 'maintenance' ? (
                            <Badge className="bg-purple-100 text-purple-800">
                              {item.form_type}
                            </Badge>
                          ) : (
                            <Badge className={
                              item.type === 'pretrip' ? 'bg-emerald-100 text-emerald-800'
                                : item.type === 'checkout' ? 'bg-amber-100 text-amber-800' : 'bg-blue-100 text-blue-800'
                            }>
                              {item.type === 'pretrip' ? 'Pre-Trip' : item.type === 'checkout' ? 'Check Out' : 'Check In'}
                            </Badge>
                          )}
                          <Badge className="bg-green-100 text-green-800">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Approved
                          </Badge>
                        </div>
                        <h3 className="text-lg font-bold text-slate-900 font-['Barlow_Condensed']">
                          {item.source === 'maintenance' 
                            ? `${item.equipment_name} (${item.form_number})`
                            : `${item.asset_name} (${item.asset_number})`
                          }
                        </h3>
                        <p className="text-sm text-slate-500">
                          Submitted: {item.source === 'maintenance' ? item.submitter_name : item.user_name} • {formatDateET(item.created_at || item.updated_at)}
                        </p>
                        <p className="text-sm text-green-700">
                          Approved by: {item.reviewer_name || item.reviewed_by_name} • {formatDateTimeET(item.reviewer_signed_at || item.reviewed_at)}
                        </p>
                      </div>
                      {/* Action Buttons - Show for all items */}
                      <div className="flex gap-1">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          className="h-11 w-11 p-0 text-slate-500 hover:bg-slate-100"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (item.source === 'asset') {
                              handleViewAssetTransaction(item);
                            } else {
                              handleViewForm(item);
                            }
                          }}
                          data-testid={`view-transaction-btn-${item.id}`}
                          title="View"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          className="h-11 w-11 p-0 text-slate-500 hover:bg-slate-100"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/maintenance/${item.id}/preview`);
                          }}
                          title="PDF"
                        >
                          <Printer className="w-4 h-4" />
                        </Button>
                        {item.source === 'asset' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-11 w-11 p-0 text-slate-500 hover:bg-slate-100"
                            data-testid={`email-pdf-btn-${item.id}`}
                            title="Email me the PDF"
                            onClick={async (e) => {
                              e.stopPropagation();
                              try {
                                const res = await axios.post(`${API}/assets/transactions/${item.id}/email-pdf`);
                                toast.success(res.data?.message || 'PDF is on its way to your email');
                              } catch (err) {
                                toast.error(err.response?.data?.detail || 'Failed to email the PDF');
                              }
                            }}
                          >
                            <Mail className="w-4 h-4" />
                          </Button>
                        )}
                        {canDeleteForms && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-11 w-11 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(item.id, item.source);
                            }}
                            data-testid={`delete-approved-btn-${item.id}`}
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {(approvedSkip.asset < approvedTotals.asset || approvedSkip.form < approvedTotals.form) && (
            <div className="flex justify-center">
              <Button
                variant="outline"
                onClick={() => fetchApprovedTransactions(false)}
                disabled={loadingMoreApproved}
                data-testid="load-more-approved-btn"
              >
                {loadingMoreApproved ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Loading...</>
                ) : (
                  `Load More (${approvedTransactions.length} of ${approvedTotals.asset + approvedTotals.form})`
                )}
              </Button>
            </div>
          )}
        </div>
      ) : activeTab === 'rejected' ? (
        /* Rejected Tab Content */
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-slate-900 font-['Barlow_Condensed']">Rejected Forms</h2>
              <p className="text-slate-500 text-sm">Forms that were rejected (requires follow-up)</p>
            </div>
            <div className="flex items-center gap-3">
              <QueueSortSelect value={rejectedSortBy} onChange={setRejectedSortBy} testId="rejected-sort-select" />
              <ViewModeToggle value={viewMode} onChange={setViewMode} />
            </div>
          </div>
          
          {rejectedTransactions.length === 0 ? (
            <div className="text-center py-12 bg-slate-50 rounded-lg">
              <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">No rejected forms</p>
            </div>
          ) : viewMode === 'list' ? (
            <Card className="border-slate-200">
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Type</TableHead>
                        <TableHead>Item</TableHead>
                        <TableHead className="hidden lg:table-cell">Date</TableHead>
                        <TableHead className="hidden lg:table-cell">Submitted By</TableHead>
                        <TableHead className="hidden lg:table-cell">Rejected By</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {sortedRejectedTransactions.map(item => (
                        <TableRow key={item.id} data-testid={`rejected-list-row-${item.id}`}>
                          <TableCell>
                            {item.source === 'maintenance' ? (
                              <Badge className="bg-purple-100 text-purple-800">{item.form_type}</Badge>
                            ) : (
                              <Badge className={
                                item.type === 'pretrip' ? 'bg-emerald-100 text-emerald-800'
                                  : item.type === 'checkout' ? 'bg-amber-100 text-amber-800' : 'bg-blue-100 text-blue-800'
                              }>
                                {item.type === 'pretrip' ? 'Pre-Trip' : item.type === 'checkout' ? 'Check Out' : 'Check In'}
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell className="font-medium">
                            {item.source === 'maintenance'
                              ? `${item.equipment_name} (${item.form_number})`
                              : `${item.asset_name} (${item.asset_number})`}
                          </TableCell>
                          <TableCell className="hidden lg:table-cell text-sm">
                            {formatDateET(item.created_at || item.updated_at)}
                          </TableCell>
                          <TableCell className="hidden lg:table-cell text-sm">
                            {item.source === 'maintenance' ? item.submitter_name : item.user_name}
                          </TableCell>
                          <TableCell className="hidden lg:table-cell text-sm text-red-700">
                            {item.rejected_by || item.reviewed_by_name}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <Button variant="ghost" size="sm" className="h-11 w-11 p-0 text-slate-500 hover:bg-slate-100" title="View"
                                onClick={() => item.source === 'asset' ? handleViewAssetTransaction(item) : handleViewForm(item)}>
                                <Eye className="w-4 h-4" />
                              </Button>
                              <Button variant="ghost" size="sm" className="h-11 w-11 p-0 text-slate-500 hover:bg-slate-100" title="PDF"
                                onClick={() => navigate(`/maintenance/${item.id}/preview`)}>
                                <Printer className="w-4 h-4" />
                              </Button>
                              {(isMaintenanceManager || user?.id === (item.source === 'maintenance' ? (item.submitted_by_id || item.created_by_id) : item.user_id)) && (
                                <Button variant="ghost" size="sm" className="h-11 w-11 p-0 text-green-600 hover:text-green-700 hover:bg-green-50" title="Resubmit"
                                  onClick={() => handleEditPendingItem(item)} data-testid={`resubmit-rejected-btn-${item.id}`}>
                                  <RotateCcw className="w-4 h-4" />
                                </Button>
                              )}
                              {canDeleteForms && (
                                <Button variant="ghost" size="sm" className="h-11 w-11 p-0 text-red-600 hover:text-red-700 hover:bg-red-50" title="Delete"
                                  onClick={() => handleDelete(item.id, item.source)}>
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {sortedRejectedTransactions.map(item => (
                <Card key={item.id} className="border-red-200 bg-red-50/30">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          {item.source === 'maintenance' ? (
                            <Badge className="bg-purple-100 text-purple-800">
                              {item.form_type}
                            </Badge>
                          ) : (
                            <Badge className={
                              item.type === 'pretrip' ? 'bg-emerald-100 text-emerald-800'
                                : item.type === 'checkout' ? 'bg-amber-100 text-amber-800' : 'bg-blue-100 text-blue-800'
                            }>
                              {item.type === 'pretrip' ? 'Pre-Trip' : item.type === 'checkout' ? 'Check Out' : 'Check In'}
                            </Badge>
                          )}
                          <Badge className="bg-red-100 text-red-800">
                            <XCircle className="w-3 h-3 mr-1" />
                            Rejected
                          </Badge>
                        </div>
                        <h3 className="text-lg font-bold text-slate-900 font-['Barlow_Condensed']">
                          {item.source === 'maintenance' 
                            ? `${item.equipment_name} (${item.form_number})`
                            : `${item.asset_name} (${item.asset_number})`
                          }
                        </h3>
                        <p className="text-sm text-slate-500">
                          Submitted: {item.source === 'maintenance' ? item.submitter_name : item.user_name} • {formatDateET(item.created_at || item.updated_at)}
                        </p>
                        <p className="text-sm text-red-700">
                          Rejected by: {item.rejected_by || item.reviewed_by_name} • {formatDateTimeET(item.rejected_at || item.reviewed_at)}
                        </p>
                        {(item.rejection_reason) && (
                          <p className="text-sm text-red-600 mt-2 p-2 bg-red-100 rounded">
                            <strong>Reason:</strong> {item.rejection_reason}
                          </p>
                        )}
                      </div>
                      {/* Action Buttons - Show for all items */}
                      <div className="flex gap-1">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          className="h-11 w-11 p-0 text-slate-500 hover:bg-slate-100"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (item.source === 'asset') {
                              handleViewAssetTransaction(item);
                            } else {
                              handleViewForm(item);
                            }
                          }}
                          data-testid={`view-transaction-btn-${item.id}`}
                          title="View"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          className="h-11 w-11 p-0 text-slate-500 hover:bg-slate-100"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/maintenance/${item.id}/preview`);
                          }}
                          title="PDF"
                        >
                          <Printer className="w-4 h-4" />
                        </Button>
                        {(isMaintenanceManager || user?.id === (item.source === 'maintenance' ? (item.submitted_by_id || item.created_by_id) : item.user_id)) && (
                          <Button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditPendingItem(item);
                            }}
                            className="bg-green-600 hover:bg-green-500 text-white"
                            data-testid={`resubmit-rejected-card-btn-${item.id}`}
                          >
                            <RotateCcw className="w-4 h-4 mr-2" />
                            Resubmit
                          </Button>
                        )}
                        {canDeleteForms && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-11 w-11 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(item.id, item.source);
                            }}
                            data-testid={`delete-rejected-btn-${item.id}`}
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {(rejectedSkip.asset < rejectedTotals.asset || rejectedSkip.form < rejectedTotals.form) && (
            <div className="flex justify-center">
              <Button
                variant="outline"
                onClick={() => fetchRejectedTransactions(false)}
                disabled={loadingMoreRejected}
                data-testid="load-more-rejected-btn"
              >
                {loadingMoreRejected ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Loading...</>
                ) : (
                  `Load More (${rejectedTransactions.length} of ${rejectedTotals.asset + rejectedTotals.form})`
                )}
              </Button>
            </div>
          )}
        </div>
      ) : activeTab === 'archive' ? (
        <div className="space-y-4">
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 items-end">
                <div>
                  <Label className="text-xs text-slate-500">From</Label>
                  <Input
                    type="date"
                    value={archiveFilters.startDate}
                    onChange={(e) => setArchiveFilters({ ...archiveFilters, startDate: e.target.value })}
                    data-testid="archive-start-date"
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">To</Label>
                  <Input
                    type="date"
                    value={archiveFilters.endDate}
                    onChange={(e) => setArchiveFilters({ ...archiveFilters, endDate: e.target.value })}
                    data-testid="archive-end-date"
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Equipment</Label>
                  <Input
                    placeholder="Search equipment..."
                    value={archiveFilters.equipment}
                    onChange={(e) => setArchiveFilters({ ...archiveFilters, equipment: e.target.value })}
                    data-testid="archive-equipment-input"
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Form Type</Label>
                  <Select value={archiveFilters.formType} onValueChange={(v) => setArchiveFilters({ ...archiveFilters, formType: v })}>
                    <SelectTrigger data-testid="archive-form-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Types</SelectItem>
                      <SelectItem value="Grease">Grease Form</SelectItem>
                      <SelectItem value="Maintenance">Maintenance Request Form</SelectItem>
                      <SelectItem value="Confined Space Evaluation">Confined Space Evaluation</SelectItem>
                      <SelectItem value="Equipment Check In">Equipment Check In</SelectItem>
                      <SelectItem value="Equipment Check Out">Equipment Check Out</SelectItem>
                      <SelectItem value="Pre-Trip Inspection">Pre-Trip Inspection</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Submitted By</Label>
                  <div className="flex gap-2">
                    <Input
                      placeholder="Search name..."
                      value={archiveFilters.submittedBy}
                      onChange={(e) => setArchiveFilters({ ...archiveFilters, submittedBy: e.target.value })}
                      data-testid="archive-submitted-by-input"
                    />
                    <Button onClick={() => fetchArchive(false)} disabled={archiveLoading} data-testid="archive-search-btn">
                      <Search className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
              <div className="flex flex-wrap items-center justify-between gap-3 mt-3 pt-3 border-t border-slate-100">
                <div className="flex flex-wrap gap-2">
                  {[
                    { key: 'this_week', label: 'This Week' },
                    { key: 'last_week', label: 'Last Week' },
                    { key: 'this_month', label: 'This Month' },
                    { key: 'last_month', label: 'Last Month' },
                  ].map((preset) => (
                    <Button
                      key={preset.key}
                      variant="outline"
                      size="sm"
                      className="text-xs h-8"
                      onClick={() => applyArchiveDatePreset(preset.key)}
                      data-testid={`archive-preset-${preset.key}`}
                    >
                      {preset.label}
                    </Button>
                  ))}
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={archiveSelectedKeys.size === 0 || archiveDownloading}
                    onClick={handleDownloadSelected}
                    data-testid="archive-download-selected-btn"
                  >
                    {archiveDownloading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                    Download Selected ({archiveSelectedKeys.size})
                  </Button>
                  <Button
                    size="sm"
                    className="bg-amber-600 hover:bg-amber-500 text-white"
                    disabled={archiveDownloading}
                    onClick={handleDownloadFiltered}
                    data-testid="archive-download-filtered-btn"
                  >
                    {archiveDownloading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
                    Download All (Filtered)
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {archiveLoading && archiveItems.length === 0 ? (
            <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
          ) : archiveItems.length === 0 ? (
            <div className="text-center py-12 bg-slate-50 rounded-lg">
              <FolderOpen className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">No archived forms match these filters</p>
            </div>
          ) : (
            <Card className="border-slate-200">
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-10">
                          <Checkbox
                            checked={archiveItems.length > 0 && archiveItems.every((item) => archiveSelectedKeys.has(archiveKey(item)))}
                            onCheckedChange={toggleArchiveSelectAllVisible}
                            data-testid="archive-select-all-checkbox"
                          />
                        </TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Equipment</TableHead>
                        <TableHead className="hidden lg:table-cell">Submitted By</TableHead>
                        <TableHead className="hidden lg:table-cell">Approved By</TableHead>
                        <TableHead>Approved</TableHead>
                        <TableHead>PDF</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {archiveItems.map((item) => (
                        <TableRow key={`${item.source}-${item.id}`} data-testid={`archive-row-${item.id}`}>
                          <TableCell>
                            <Checkbox
                              checked={archiveSelectedKeys.has(archiveKey(item))}
                              onCheckedChange={() => toggleArchiveSelect(item)}
                              data-testid={`archive-select-${item.id}`}
                            />
                          </TableCell>
                          <TableCell className="text-sm">{item.form_type}</TableCell>
                          <TableCell className="text-sm">{item.equipment_name} {item.form_number ? `(${item.form_number})` : ''}</TableCell>
                          <TableCell className="hidden lg:table-cell text-sm">{item.submitted_by}</TableCell>
                          <TableCell className="hidden lg:table-cell text-sm">{item.approved_by}</TableCell>
                          <TableCell className="text-sm">{formatDateET(item.approved_at)}</TableCell>
                          <TableCell>
                            {item.archived_pdf_url ? (
                              <a
                                href={item.archived_pdf_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-amber-700 hover:text-amber-800 font-medium text-sm flex items-center gap-1"
                                data-testid={`archive-pdf-link-${item.id}`}
                              >
                                <FolderOpen className="w-4 h-4" /> Open
                              </a>
                            ) : (
                              <Button
                                variant="outline"
                                size="sm"
                                className="text-xs h-8 border-amber-300 text-amber-700 hover:bg-amber-50"
                                onClick={() => handleArchivePdfNow(item)}
                                disabled={archivingId === item.id}
                                data-testid={`archive-pdf-generate-${item.id}`}
                              >
                                {archivingId === item.id ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
                                Not archived — click to generate
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          )}

          {archiveItems.length < archiveTotal && (
            <div className="flex justify-center">
              <Button variant="outline" onClick={() => fetchArchive(true)} disabled={archiveLoading} data-testid="load-more-archive-btn">
                {archiveLoading ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Loading...</>
                ) : (
                  `Load More (${archiveItems.length} of ${archiveTotal})`
                )}
              </Button>
            </div>
          )}
        </div>
      ) : activeTab === 'maintenance' ? (
        renderFormTypeTab('Maintenance', 'Maintenance Request')
      ) : activeTab === 'grease' ? (
        renderFormTypeTab('Grease', 'Grease Form')
      ) : null}

      {/* Dialog - Outside of tab conditionals so it shows on any tab */}
      <Dialog open={showDialog} onOpenChange={(open) => { if (!open) handleCloseDialog(); else setShowDialog(open); }}>
        <DialogContent
          className="w-[calc(100vw-2rem)] max-w-2xl max-h-[90vh] overflow-y-auto"
          // Fix (2026-08-30): the photo lightbox renders as its own
          // full-screen overlay OUTSIDE this DialogContent's DOM
          // subtree. Radix's Dialog treats any click there as a
          // "click outside" and was closing (and wiping) this form
          // dialog behind it — the exact reported data-loss bug.
          // Ignore outside-interaction/escape while the lightbox is
          // open; PhotoLightbox handles its own Esc/close.
          onPointerDownOutside={(e) => { if (lightboxPhoto) e.preventDefault(); }}
          onInteractOutside={(e) => { if (lightboxPhoto) e.preventDefault(); }}
          onEscapeKeyDown={(e) => { if (lightboxPhoto) e.preventDefault(); }}
        >
          <DialogHeader>
            <DialogTitle className="font-['Barlow_Condensed'] text-lg">
              {isViewOnly ? 'View Maintenance Form' : (editingForm ? 'Edit Maintenance Form' : 'New Maintenance Form')}
            </DialogTitle>
            {editingForm && (
              <p className="text-xs text-amber-600 font-mono">{editingForm.form_number}</p>
            )}
          </DialogHeader>
          <div className={`space-y-4 pt-2 ${isViewOnly ? 'pointer-events-none opacity-80' : ''}`}>
            {/* Round 6: Job header on Grease + Maintenance Request Form
                (parity with PreCon/Locate/Soft Dig/CSE). Only shown for
                template-backed types that support job linkage — the
                asset-linked equipment forms use their own JobSelectionCard
                on the AssetDetail page. */}
            {(formType === 'Grease' || formType === 'Maintenance') && (
              <JobSelectionCard
                value={selectedJobId}
                onChange={setSelectedJobId}
                availableJobs={availableJobs}
                loadingJobs={loadingJobs}
                selectedJob={selectedJob}
                myJobIds={myJobIds}
                required={false}
                disabled={isViewOnly}
                testIdPrefix={`${formType.toLowerCase()}-job-selection`}
                summaryVariant="detailed"
              />
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-sm">Form Type *</Label>
                <Select
                  value={formType}
                  onValueChange={handleTypeChange}
                  disabled={isViewOnly || editingForm?.source === 'asset'}
                >
                  <SelectTrigger data-testid="form-type-select" className="h-11">
                    <SelectValue placeholder="Select form type" />
                  </SelectTrigger>
                  <SelectContent>
                    {FORM_TYPES.map(type => (
                      <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                    ))}
                    {/* Fix (2026-08-30, generalized 2026-09-08): any
                        existing form whose form_type is no longer in
                        the creatable FORM_TYPES list (asset-sourced
                        Check In/Out/Pre-Trip, and now Confined Space
                        Evaluation) needs a matching SelectItem when
                        VIEWING it, or SelectValue has nothing to
                        display and renders blank. Only appears
                        (disabled) for an existing form of a retired
                        type — never offered when creating a new one. */}
                    {formType && !FORM_TYPES.some(t => t.value === formType) && (
                      <SelectItem value={formType}>{formType}</SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm">Date *</Label>
                {/* Fix (2026-08-30): items #1/#2 from the polish batch —
                    shadcn calendar picker for genuine maintenance forms,
                    plain read-only text (not a picker at all) for
                    asset-sourced forms since edits there were already
                    disabled but a native date input still visually
                    implied you could change it. */}
                {editingForm?.source === 'asset' ? (
                  <div
                    className="h-11 flex items-center px-3 text-slate-600 text-sm"
                    title="The original transaction date — not editable here"
                    data-testid="form-date-readonly"
                  >
                    {formDate ? format(parseLocalDate(formDate), 'MM/dd/yyyy') : '—'}
                  </div>
                ) : (
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        type="button"
                        variant="outline"
                        disabled={isViewOnly}
                        className={`w-full h-11 justify-start text-left font-normal bg-slate-50 ${!formDate ? 'text-slate-400' : ''}`}
                        data-testid="form-date-input"
                      >
                        <CalendarIcon className="w-4 h-4 mr-2 shrink-0" />
                        {formDate ? format(parseLocalDate(formDate), 'MM/dd/yyyy') : 'Select date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <CalendarWidget
                        mode="single"
                        selected={formDate ? parseLocalDate(formDate) : undefined}
                        onSelect={(date) => date && setFormDate(format(date, 'yyyy-MM-dd'))}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                )}
              </div>
            </div>

            {/* Equipment picker — hidden for Confined Space Evaluation
                (safety permit, not tied to equipment). Every other
                form type still requires an equipment link. */}
            {formType !== 'Confined Space Evaluation' && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-sm">Select Equipment *</Label>
                  <Select
                    value={selectedEquipment?.id || (useCustomEquipment ? 'custom' : '')}
                    onValueChange={handleEquipmentSelect}
                    disabled={isViewOnly}
                  >
                    <SelectTrigger className="bg-slate-50" data-testid="equipment-select-trigger">
                      <SelectValue placeholder="Select equipment or enter custom" />
                    </SelectTrigger>
                    <SelectContent>
                      {equipmentList.map(equip => (
                        <SelectItem key={equip.id} value={equip.id}>
                          {equip.name} {(equip.equipment_type || equip.category) && `(${equip.equipment_type || equip.category})`}
                        </SelectItem>
                      ))}
                      <SelectItem value="custom">-- Enter Custom Equipment --</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>{useCustomEquipment ? 'Equipment Name *' : 'Equipment Name'}</Label>
                  <Input
                    value={equipmentName}
                    onChange={(e) => setEquipmentName(e.target.value)}
                    placeholder={useCustomEquipment ? "Enter equipment name" : "Auto-filled from selection"}
                    className="bg-slate-50"
                    data-testid="equipment-name-input"
                    disabled={isViewOnly || (!useCustomEquipment && selectedEquipment)}
                  />
                </div>
              </div>
            )}

            {formType !== 'Confined Space Evaluation' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Equipment ID (VIN/Serial/Model)</Label>
                <Input
                  value={equipmentId}
                  onChange={(e) => setEquipmentId(e.target.value)}
                  placeholder={useCustomEquipment ? "VIN, Serial #, Model #" : "Auto-filled from selection"}
                  className="bg-slate-50"
                  disabled={isViewOnly || (!useCustomEquipment && selectedEquipment)}
                />
              </div>
              <div className="space-y-2">
                <Label>Other / Manual Entry</Label>
                <Input
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Additional details..."
                  className="bg-slate-50"
                  disabled={editingForm?.source === 'asset'}
                  title={editingForm?.source === 'asset' ? 'Use Additional Notes below for corrections' : undefined}
                />
              </div>
            </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Performed By</Label>
                <Input
                  value={performedBy}
                  onChange={(e) => setPerformedBy(e.target.value)}
                  className="bg-slate-50"
                  disabled={isViewOnly}
                />
              </div>
              {formType !== 'Confined Space Evaluation' && (
                <>
                  <div className="space-y-2">
                    <Label>Mileage</Label>
                    <Input
                      type="number"
                      value={mileage}
                      onChange={(e) => setMileage(e.target.value)}
                      className="bg-slate-50"
                      disabled={editingForm?.source === 'asset'}
                      title={editingForm?.source === 'asset' ? 'Recorded from the original inspection checklist — not editable here' : undefined}
                      data-testid="maintenance-mileage-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Condition</Label>
                    <Select
                      value={condition}
                      onValueChange={setCondition}
                      disabled={editingForm?.source === 'asset'}
                    >
                      <SelectTrigger data-testid="maintenance-condition-select">
                        <SelectValue placeholder="Select condition" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Excellent">Excellent</SelectItem>
                        <SelectItem value="Good">Good</SelectItem>
                        <SelectItem value="Fair">Fair</SelectItem>
                        <SelectItem value="Poor">Poor</SelectItem>
                        <SelectItem value="Needs Repair">Needs Repair</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}
            </div>

            {/* Template-backed forms (Grease, Maintenance Request
                Form, Confined Space Evaluation) render via the shared
                ChecklistRenderer — option hydration, repeatable
                sections, inline signature widget all work out of the
                box. Legacy forms keep the custom renderer below. */}
            {templateFlatItems.length > 0 ? (
              <div className="space-y-4">
                <div className="flex items-center gap-2 flex-wrap">
                  <Label className="text-base font-semibold">
                    {templateTitle || 'Checklist'}
                  </Label>
                  {templateUpdatedAt && (
                    <span className="text-xs text-slate-400" data-testid="maintenance-checklist-updated-at">
                      Updated: {new Date(templateUpdatedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </span>
                  )}
                </div>
                <ChecklistRenderer
                  flatItems={templateFlatItems}
                  values={templateValues}
                  onChange={(idx, field, value) => {
                    setTemplateValues(prev => ({
                      ...prev,
                      [idx]: { ...(prev[idx] || {}), [field]: value },
                    }));
                  }}
                  onPhotoUpload={(idx, file) => handleTemplatePhotoUpload(idx, file)}
                  uploadingPhotoIdx={uploadingPhoto}
                  onPhotoRemove={(idx, pIdx) => {
                    setTemplateValues(prev => ({
                      ...prev,
                      [idx]: {
                        ...(prev[idx] || {}),
                        photos: ((prev[idx] || {}).photos || []).filter((_, i) => i !== pIdx),
                      },
                    }));
                  }}
                  onPhotoClick={(photos, pIdx) => openLightbox(photos[pIdx], photos, pIdx)}
                  teamMembers={teamMembers}
                  assetsForSelect={assetsForSelect}
                  readOnly={isViewOnly}
                  testIdPrefix="mform"
                />
              </div>
            ) : checklist.length > 0 && (
              <div className="space-y-4">
                <Label className="text-base font-semibold">Checklist</Label>
                {editingForm?.source === 'asset' && !isViewOnly && (
                  <p className="text-xs text-slate-500 -mt-2">
                    Recorded during the original inspection — read-only here. Use Notes above for corrections.
                  </p>
                )}
                <div className="space-y-3">
                  {checklist.map((item, idx) => (
                    <div key={idx} className="p-3 bg-slate-50 rounded-lg space-y-3" data-testid={`dialog-checklist-item-${idx}`}>
                      <div className="flex items-start gap-3">
                        <Checkbox
                          id={`check-${idx}`}
                          checked={item.checked}
                          onCheckedChange={(checked) => handleChecklistChange(idx, 'checked', checked)}
                          disabled={editingForm?.source === 'asset'}
                        />
                        <div className="flex-1 space-y-2">
                          <Label htmlFor={`check-${idx}`} className="cursor-pointer" data-testid={`dialog-checklist-label-${idx}`}>
                            {item.item}
                          </Label>
                          <Input
                            placeholder="Notes (optional)"
                            value={item.notes}
                            onChange={(e) => handleChecklistChange(idx, 'notes', e.target.value)}
                            className="bg-white text-sm"
                            disabled={editingForm?.source === 'asset'}
                          />
                        </div>
                        {/* Fix 3 (2026-08-30): the upload button used to render
                            unconditionally on every row regardless of whether
                            that checklist item was configured for photos
                            (`item.photo === true`, same flag ChecklistRenderer
                            already gates on for template-backed forms). Legacy
                            default checklists (getDefaultChecklist) never set
                            this flag, so this hides the button for those and
                            only shows it for items the template actually
                            configured for photo capture. */}
                        {item.photo && (
                          <div className="flex items-center gap-1">
                            <input
                              type="file"
                              accept="image/*"
                              className="hidden"
                              ref={el => photoInputRefs.current[idx] = el}
                              onChange={(e) => {
                                if (e.target.files?.[0]) {
                                  handlePhotoUpload(idx, e.target.files[0]);
                                  e.target.value = '';
                                }
                              }}
                            />
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => photoInputRefs.current[idx]?.click()}
                              disabled={uploadingPhoto === idx || editingForm?.source === 'asset'}
                              title="Upload photo"
                              className="h-11 w-11 p-0"
                              data-testid={`checklist-photo-btn-${idx}`}
                            >
                              {uploadingPhoto === idx ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Camera className="w-4 h-4" />
                              )}
                            </Button>
                            {item.photos?.length > 0 && (
                              <span className="text-xs text-slate-500 bg-slate-200 px-1.5 py-0.5 rounded">
                                {item.photos.length}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                      
                      {/* Display Photos */}
                      {item.photos?.length > 0 && (
                        <div className="flex flex-wrap gap-2 ml-7">
                          {item.photos.map((photoUrl, photoIdx) => (
                            <div key={photoIdx} className="relative group">
                              <button
                                type="button"
                                onClick={() => openLightbox(photoUrl, item.photos, photoIdx)}
                                data-testid={`checklist-photo-thumb-${idx}-${photoIdx}`}
                              >
                                <SafeImage
                                  src={resolveFileUrl(photoUrl, 'preview')} 
                                  alt={`Photo ${photoIdx + 1}`}
                                  loading="lazy"
                                  decoding="async"
                                  className="w-48 h-48 object-cover rounded border-2 border-slate-200 hover:border-amber-400 transition-colors"
                                />
                              </button>
                              <button
                                type="button"
                                onClick={() => handlePhotoDelete(idx, photoUrl)}
                                className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                                title="Delete photo"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label>Additional Notes</Label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                className="bg-slate-50"
                placeholder="Any additional observations or notes..."
                data-testid="maintenance-notes-textarea"
                disabled={isViewOnly}
              />
            </div>

            {/* Files panel — visible only in view mode for saved forms.
                Groups the archived PDF, both signatures, and every
                checklist photo into one auditor-friendly list so office
                staff don't have to hunt through OneDrive to see what
                was captured. Photos open the existing lightbox for
                zoom / navigation. 2026-10-03: now covers asset-sourced
                (Check In/Out/Pre-Trip) forms too — GET /maintenance/{id}/
                files falls back to asset_transactions when the id isn't
                a maintenance_forms record. */}
            {isViewOnly && editingForm?.id && (
              <div className="space-y-2 pt-2 border-t border-slate-200" data-testid="maintenance-files-section">
                <Label className="font-semibold flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Files
                  {filesList != null && (
                    <span className="text-xs font-normal text-slate-500">
                      ({filesList.length})
                    </span>
                  )}
                </Label>
                {filesLoading ? (
                  <div className="flex items-center gap-2 text-sm text-slate-500 p-3 bg-slate-50 rounded-md">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Loading files…
                  </div>
                ) : filesList == null || filesList.length === 0 ? (
                  <p className="text-sm text-slate-500 p-3 bg-slate-50 rounded-md">
                    No files attached to this form yet.
                  </p>
                ) : (
                  <div className="space-y-1.5">
                    {filesList.map((f, i) => {
                      const isPhoto = f.kind === 'photo' || f.kind === 'signature';
                      const badgeTone =
                        f.kind === 'archived_pdf'
                          ? 'bg-amber-100 text-amber-800 border-amber-200'
                          : f.kind === 'signature'
                            ? 'bg-blue-100 text-blue-800 border-blue-200'
                            : 'bg-slate-100 text-slate-700 border-slate-200';
                      const badgeText =
                        f.kind === 'archived_pdf' ? 'Approved PDF'
                          : f.kind === 'signature' ? 'Signature'
                            : 'Photo';
                      const photoUrls = filesList
                        .filter((x) => x.kind === 'photo')
                        .map((x) => x.url);
                      const openTarget = () => {
                        if (isPhoto && f.kind === 'photo') {
                          const idx = Math.max(0, photoUrls.indexOf(f.url));
                          openLightbox(f.url, photoUrls, idx);
                        } else {
                          window.open(f.url, '_blank', 'noopener,noreferrer');
                        }
                      };
                      return (
                        <button
                          key={`${f.kind}-${i}`}
                          type="button"
                          onClick={openTarget}
                          className="w-full flex items-center gap-3 p-2 bg-white border border-slate-200 rounded-md hover:border-slate-300 hover:bg-slate-50 text-left transition-colors pointer-events-auto"
                          data-testid={`maintenance-file-${f.kind}-${i}`}
                        >
                          <span className={`text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded border ${badgeTone} flex-shrink-0`}>
                            {badgeText}
                          </span>
                          <span className="flex-1 text-sm text-slate-800 truncate">{f.label}</span>
                          {f.uploaded_at && (
                            <span className="text-xs text-slate-400 flex-shrink-0">
                              {formatDateET(f.uploaded_at)}
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Submitted By / Signatures.
                Bug #2 fix (2026-09-06): this block used to always
                render the blank drawing canvas, even for a form that
                already had a saved submitter/reviewer signature — the
                real saved signature lived correctly in the DB (Files
                panel, Pending Review thumbnail) but was invisible
                here. Now: if a signature was already captured, show
                it as an image with name + timestamp (read-only); the
                interactive canvas only appears for a brand-new form
                that hasn't been signed/submitted yet. */}
            {(editingForm?.submitter_signature_url || editingForm?.signature_url) ? (
              <div className="space-y-2 pt-4 border-t border-slate-200">
                <Label className="font-semibold">Signatures</Label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="p-3 bg-slate-100 border border-slate-200 rounded-lg">
                    <p className="text-xs font-medium text-slate-500 mb-1">Submitted By</p>
                    <img
                      src={editingForm.submitter_signature_url || editingForm.signature_url}
                      alt="Submitter Signature"
                      className="h-12 mb-1 border border-slate-200 rounded bg-white"
                      data-testid="maintenance-submitter-signature-img"
                    />
                    <p className="text-sm font-medium text-slate-800">{editingForm.submitter_name || editingForm.performed_by || '—'}</p>
                    <p className="text-xs text-slate-500">{formatDateTimeET(editingForm.submitter_signed_at || editingForm.submitted_at || editingForm.created_at)}</p>
                  </div>
                  <div className={`p-3 border rounded-lg ${editingForm.reviewer_signature_url ? 'bg-green-50 border-green-200' : 'bg-slate-100 border-slate-200'}`}>
                    <p className="text-xs font-medium text-slate-500 mb-1">Manager Approval</p>
                    {editingForm.reviewer_signature_url ? (
                      <>
                        <img
                          src={editingForm.reviewer_signature_url}
                          alt="Reviewer Signature"
                          className="h-12 mb-1 border border-green-200 rounded bg-white"
                          data-testid="maintenance-reviewer-signature-img"
                        />
                        <p className="text-sm font-medium text-green-700">{editingForm.reviewer_name || editingForm.reviewed_by_name || '—'}</p>
                        <p className="text-xs text-green-600">{formatDateTimeET(editingForm.reviewer_signed_at || editingForm.reviewed_at)}</p>
                      </>
                    ) : (
                      <p className="text-sm text-slate-400 italic">Awaiting approval</p>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <>
                <div className="space-y-2 pt-4 border-t border-slate-200">
                  <Label className="font-semibold">Submitted By</Label>
                  <div className="p-3 bg-slate-100 border border-slate-200 rounded-lg">
                    <p className="font-medium text-slate-800">{user?.full_name}</p>
                    <p className="text-sm text-slate-500">{user?.email}</p>
                  </div>
                </div>

                {/* Signature Pad */}
                <div className="space-y-2">
                  <Label className="font-semibold">Signature <span className="text-red-500">*</span></Label>
                  <div className="border-2 border-dashed border-slate-300 rounded-lg p-2 bg-white">
                    <canvas
                      ref={signatureCanvasRef}
                      width={400}
                      height={150}
                      className="w-full border border-slate-200 rounded cursor-crosshair touch-none"
                      data-testid="maintenance-signature-canvas"
                      onMouseDown={startDrawing}
                      onMouseMove={draw}
                      onMouseUp={stopDrawing}
                      onMouseLeave={stopDrawing}
                      onTouchStart={startDrawing}
                      onTouchMove={draw}
                      onTouchEnd={stopDrawing}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={clearSignature}
                      className="mt-2 text-slate-500"
                    >
                      Clear Signature
                    </Button>
                  </div>
                  {!hasSignature && (
                    <p className="text-xs text-amber-600">Sign above to submit for approval</p>
                  )}
                </div>
              </>
            )}

          </div>
          {/* Footer sits OUTSIDE the pointer-events-none wrapper so
              Close / Edit Form / Save buttons remain clickable in
              view-only mode. */}
          <div className="flex justify-end gap-3 pt-4">
              <Button variant="outline" onClick={handleCloseDialog} data-testid="close-maintenance-dialog-btn">
                {isViewOnly ? 'Close' : 'Cancel'}
              </Button>
              {/* Round 6 Option A — admin unlock on an already-saved
                  form. Flips isViewOnly off so the same dialog becomes
                  editable; save PUTs to the same endpoint which stamps
                  last_edited_by/at when status is approved. */}
              {isViewOnly && editingForm && (user?.role === 'admin' || user?.role === 'super_user') && (
                <Button
                  variant="outline"
                  className="border-amber-400 text-amber-700 hover:bg-amber-50"
                  onClick={() => setIsViewOnly(false)}
                  data-testid="edit-maintenance-form-btn"
                >
                  Edit Form
                </Button>
              )}
              {!isViewOnly && editingForm && editingForm.status === 'approved' && (
                <Button
                  onClick={handleSave}
                  disabled={saving}
                  className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold"
                  data-testid="save-approved-maintenance-form-btn"
                >
                  {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                  Save Correction
                </Button>
              )}
              {!isViewOnly && !(editingForm && editingForm.status === 'approved') && (
                <>
                  <Button 
                    onClick={handleSave}
                    disabled={saving}
                    variant="outline"
                    className="border-slate-300"
                    data-testid="save-maintenance-draft-btn"
                  >
                    {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                    {editingForm?.source === 'asset' ? 'Save Changes' : 'Save Draft'}
                  </Button>
                  {/* Fix (2026-08-30): asset-sourced pending items
                      (Check In/Out/Pre-Trip) are approved/rejected from
                      the Pending Review list's own Approve/Reject
                      buttons, not from inside this dialog — hide the
                      unrelated "Submit for Approval" workflow for them. */}
                  {editingForm?.source !== 'asset' && (
                    <Button 
                      onClick={handleSubmitForApproval}
                      disabled={saving || !hasSignature}
                      className="bg-green-600 hover:bg-green-500 text-white"
                      data-testid="submit-maintenance-form-btn"
                    >
                      {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                      {editingForm?.status === 'rejected' ? 'Resubmit for Approval' : 'Submit for Approval'}
                    </Button>
                  )}
                  {/* Resubmit a rejected Check In/Out/Pre-Trip — no
                      signature step here (decision: 2026-10-02), just
                      flips approval_status back to pending. */}
                  {editingForm?.source === 'asset' && editingForm?.status === 'rejected' && (
                    <Button
                      onClick={handleResubmitAsset}
                      disabled={saving}
                      className="bg-green-600 hover:bg-green-500 text-white"
                      data-testid="resubmit-asset-transaction-btn"
                    >
                      {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                      Resubmit for Approval
                    </Button>
                  )}
                </>
              )}
            </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {formToDelete?.source === 'asset' ? 'this record' : 'this form'}?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete {formToDelete?.source === 'asset' ? 'this equipment check-in/check-out record' : 'this maintenance form'} and any associated photos.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setFormToDelete(null)}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmDelete}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reject Confirmation Dialog */}
      <AlertDialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reject Form</AlertDialogTitle>
            <AlertDialogDescription>
              Please provide a reason for rejection. This will be visible to the person who submitted the form.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-4">
            <Textarea
              placeholder="Enter rejection reason..."
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              rows={3}
              data-testid="rejection-reason-input"
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => { setRejectingTransaction(null); setRejectionReason(''); }}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleReject}
              disabled={!rejectionReason.trim()}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="confirm-reject-btn"
            >
              Reject
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Manager Approval Dialog with Signature */}
      <Dialog open={showApprovalDialog} onOpenChange={(open) => { if (!open) { setShowApprovalDialog(false); setApprovingItem(null); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Approve Form</DialogTitle>
          </DialogHeader>
          
          {approvingItem && (
            <div className="space-y-4">
              {/* Form Details */}
              <div className="bg-slate-50 p-3 rounded-lg">
                <p className="text-sm text-slate-600">
                  <span className="font-medium">Form:</span> {approvingItem.form_type || (approvingItem.type === 'checkin' ? 'Equipment Check In' : approvingItem.type === 'pretrip' ? 'Pre-Trip Inspection' : 'Equipment Check Out')}
                </p>
                <p className="text-sm text-slate-600">
                  <span className="font-medium">Submitted by:</span> {approvingItem.performed_by_name || approvingItem.user_name}
                </p>
                <p className="text-sm text-slate-600">
                  <span className="font-medium">Equipment:</span> {approvingItem.asset_name || approvingItem.equipment_name}
                </p>
              </div>

              {/* Manager Signature */}
              <div className="space-y-2">
                <Label className="text-sm font-medium">Manager Signature *</Label>
                <p className="text-xs text-slate-500">Sign below to approve this form</p>
                <div className="border-2 border-slate-200 rounded-lg bg-white relative">
                  <canvas
                    ref={initApprovalSignatureCanvas}
                    width={350}
                    height={120}
                    className="w-full touch-none cursor-crosshair"
                    onMouseDown={handleApprovalSignatureStart}
                    onMouseMove={handleApprovalSignatureMove}
                    onMouseUp={handleApprovalSignatureEnd}
                    onMouseLeave={handleApprovalSignatureEnd}
                    onTouchStart={handleApprovalSignatureStart}
                    onTouchMove={handleApprovalSignatureMove}
                    onTouchEnd={handleApprovalSignatureEnd}
                    data-testid="approval-signature-canvas"
                  />
                  {!approvalSignature && (
                    <p className="absolute inset-0 flex items-center justify-center text-slate-300 pointer-events-none">
                      Sign here
                    </p>
                  )}
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={clearApprovalSignature}
                  className="text-xs"
                >
                  Clear Signature
                </Button>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-2 pt-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => { setShowApprovalDialog(false); setApprovingItem(null); }}
                >
                  Cancel
                </Button>
                <Button
                  className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                  onClick={submitApproval}
                  disabled={!approvalSignature || processingId === approvingItem?.id}
                  data-testid="confirm-approve-btn"
                >
                  {processingId === approvingItem?.id ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <CheckCircle className="w-4 h-4 mr-2" />
                  )}
                  Approve
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Archive Export Dialog */}
      <Dialog open={showArchiveExportDialog} onOpenChange={setShowArchiveExportDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Archive Export</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="font-medium">Form Types</Label>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { key: 'checkout', label: 'Check Out' },
                  { key: 'checkin', label: 'Check In' },
                  { key: 'pretrip', label: 'Pre-Trip' },
                  { key: 'maintenance', label: 'Maintenance' },
                ].map(({ key, label }) => (
                  <label key={key} className="flex items-center gap-2 text-sm cursor-pointer">
                    <Checkbox
                      checked={archiveExportTypes[key]}
                      onCheckedChange={(v) => setArchiveExportTypes(prev => ({ ...prev, [key]: !!v }))}
                      data-testid={`archive-export-type-${key}`}
                    />
                    {label}
                  </label>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-sm">From (optional)</Label>
                <Input
                  type="date"
                  value={archiveExportDateFrom}
                  onChange={(e) => setArchiveExportDateFrom(e.target.value)}
                  data-testid="archive-export-date-from"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-sm">To (optional)</Label>
                <Input
                  type="date"
                  value={archiveExportDateTo}
                  onChange={(e) => setArchiveExportDateTo(e.target.value)}
                  data-testid="archive-export-date-to"
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => applyArchivePreset('week')}
                data-testid="archive-export-preset-week"
              >
                This Week
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => applyArchivePreset('month')}
                data-testid="archive-export-preset-month"
              >
                This Month
              </Button>
            </div>
            <p className="text-xs text-slate-500">Leave dates blank to include every approved form of the selected types.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowArchiveExportDialog(false)} disabled={downloadingArchiveZip}>
              Cancel
            </Button>
            <Button
              onClick={handleArchiveExport}
              disabled={downloadingArchiveZip}
              className="bg-slate-900 hover:bg-slate-800 text-white"
              data-testid="archive-export-download-btn"
            >
              {downloadingArchiveZip ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <FileText className="w-4 h-4 mr-2" />}
              {downloadingArchiveZip ? 'Building ZIP…' : 'Download ZIP'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Photo Lightbox Modal — shared component (also used by
          AssetManagement.jsx/AssetDetail.js), fixes the "closing the
          enlarged photo also closes the form" data-loss bug in one
          place instead of duplicating the same lightbox markup. */}
      <PhotoLightbox
        open={!!lightboxPhoto}
        photos={lightboxPhotos}
        index={lightboxIndex}
        onClose={closeLightbox}
        onNavigate={(newIndex) => {
          setLightboxIndex(newIndex);
          setLightboxPhoto(lightboxPhotos[newIndex]);
        }}
      />
    </div>
  );
}
